import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
    AirspaceAuthorisationManager,
    airspaceAuthorisationTypesRequiringCurrentUserAsRpic,
    ExternalAuthorisationProvider,
    FlyFreelyAuthorisationProvider,
    MISSION_EDIT_INPUTS
} from '@flyfreely-portal-ui/airspace';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AerodromeDetailsDto,
    AirspaceAuthorisationDto,
    AirspaceAuthorisationService,
    AirspaceCheckCommand,
    AirspaceCheckDto,
    AirspaceJurisdictionDto,
    ApproversMissionApprovalDto,
    areRequirementsCompleted,
    AttachmentHandler,
    AuthorityRegisterSummaryDto,
    AuthorityTypeDto,
    AuthorityTypeRegisterConditionsDto,
    buildDisplayableMissionDocumentation,
    CombinedMissionStatus,
    ContactDto,
    ContactWithLocationDto,
    CurrentAttachmentVersionDto,
    DateFormatService,
    DateRange,
    defaultTimezone,
    DisplayableMissionDto,
    displayableMissionDtoCombinedStatus,
    DO_NOTHING,
    EquipmentDto,
    ExclusiveControlService,
    FEATURE_ADDITIONAL_AUTHORITIES,
    FEATURE_CASA_AUTHORISATION,
    FEATURE_EQUIPMENT,
    FEATURE_LEGACY_MISSION_APPROVALS,
    FEATURE_LOTAM,
    FEATURE_MISSION_AERODROMES,
    FEATURE_MISSION_OBJECTIVES,
    FEATURE_NOTAMS,
    FEATURE_PROJECTS,
    FEATURE_RPA_RESOURCE_ASSOCIATION,
    FEATURE_SITES,
    FEATURE_WORKGROUPS,
    findActiveApprovals,
    FlyFreelyError,
    FlyFreelyLoggingService,
    FormResponseCommand,
    fromTimestamp,
    hasFeatureFlag,
    InUseMissionWorkflowVersionDto,
    LocationDetailsDto,
    LocationFeatureDto,
    LockedMissionFields,
    MissionApprovalService,
    MissionCrewDto,
    MissionDocumentationDto,
    MissionNoticeDto,
    MissionObjectiveDto,
    MissionObjectivesService,
    MissionOperationTypeDto,
    MissionOverlapDto,
    MissionRoleDto,
    MissionService,
    MissionSummaryDto,
    NameId,
    NotamDto,
    NoticeDto,
    observeFormControl,
    OperationsSummaryService,
    Organisation,
    OrganisationAuthorityDto,
    OrganisationAuthorityGroup,
    OrganisationAuthorityService,
    PersonService,
    PersonsOrganisationDto,
    PreferencesService,
    prefillPreviousDocumentation,
    ProjectDto,
    ProjectsService,
    RequestersMissionApprovalDto,
    RpaTypeDto,
    RpaTypesService,
    SetupGuideActivityDto,
    SimpleAuthorityDto,
    SunriseSunset,
    toLocalTime,
    toLookup,
    toTimestamp,
    updateFormTreeValidity,
    UserService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FullScreenService } from '@flyfreely-portal-ui/fullscreen';
import {
    flightAreaOf,
    flightAreaPointOfFeatures,
    getFeatureGroups
} from '@flyfreely-portal-ui/locations';
import { FeatureGroup, MapDataInput } from '@flyfreely-portal-ui/map';
import { ExternalMissionsDataService } from '@flyfreely-portal-ui/missions-shared';
import { MissionNoticesDataService } from '@flyfreely-portal-ui/notices-shared';
import { OperationsSearchDataService } from '@flyfreely-portal-ui/operations-shared';
import { WorkgroupsDataService } from '@flyfreely-portal-ui/organisation-admin';
import { FormatRpaPipe } from '@flyfreely-portal-ui/resource-ui';
import { SitesDataService } from '@flyfreely-portal-ui/sites-shared';
import {
    FormatAuthorityPipe,
    LocationBearing,
    personSearch
} from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import area from '@turf/area';
import bearing from '@turf/bearing';
import center from '@turf/center';
import distance from '@turf/distance';
import { bearingToAzimuth } from '@turf/helpers';
import { Angulartics2 } from 'angulartics2';
import { deepEqual } from 'fast-equals';
import { pipe } from 'fp-ts/es6/function';
import { alt, getOrElse, map as optionMap } from 'fp-ts/es6/Option';
import { AirspaceCheckService } from 'libs/airspace/src/lib/airspace-check/airspace-check.service';
import { AirspaceDialoguesService } from 'libs/airspace/src/lib/airspace-dialogues.service';
import { calculateAuthorisationListState } from 'libs/airspace/src/lib/authorisation/authorisation-status';
import { AuthorityDialoguesService } from 'libs/authorities/src/lib/authority-dialogues.service';
import {
    isDiscontinued,
    toSimpleAuthority
} from 'libs/authorities/src/lib/helpers';
import { DocumentationStorage } from 'libs/documentation/src/lib/interfaces';
import { EquipmentDialoguesService } from 'libs/equipment/src/lib/equipment-dialogues.service';
import { WorkGroup } from 'libs/flyfreely/src/lib/services/workgroups.service';
import { AIRSPACE_DISCLAIMER } from 'libs/locations/src/lib/interfaces';
import { SetupGuideChecklistService } from 'libs/onboarding/src/lib/setup-guide/setup-guide-checklist.service';
import { PersonnelDialoguesService } from 'libs/personnel/src/lib/personnel-dialogues.service';
import { RpaDialoguesService } from 'libs/rpa/src/lib/rpa-dialogues.service';
import * as moment from 'moment';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import {
    BehaviorSubject,
    combineLatest,
    firstValueFrom,
    forkJoin,
    Observable,
    of,
    Subject,
    timer
} from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    aerodromesToFeatureGroups,
    findActiveMissionApprovals,
    getSimplifiedAuthorityFromApproval,
    missionLocationsToFeatureCollection,
    missionNoticeSort,
    notamToFeatureCollection
} from '../helpers';
import { MissionApprovalWithAuthority, MissionFormValue } from '../interfaces';
import { MissionDialoguesService } from '../mission-dialogues.service';
import { validateCrew } from '../mission-validators';
import {
    AerodromeContact,
    AerodromeFrequency
} from './aerodrome-search/aerodrome-search-dialogue.component';
import { MissionDocumentation } from './mission-documentation/mission-documentation.component';
import { MissionEditService, SelectableRpa } from './mission-edit.service';

const MISSION_FIELD_APP_INFO = 'mission-field-app-info';
const SCREEN_IDENTIFIER = 'mission-edit-v2-dialogue';

/**
 * Returns a matcher function for the given contact.
 * @param contact contact to match
 */
function matchesAerodromeContact(contact: AerodromeContact) {
    return (c: ContactDto) =>
        c.identifier === contact.aerodromeCode &&
        c.value === contact.contact &&
        c.subtype === contact.type;
}

function matchesAerodromeFrequency(frequency: AerodromeFrequency) {
    return (f: ContactDto) =>
        f.identifier === frequency.aerodromeCode &&
        f.value === frequency.frequency &&
        f.subtype === frequency.abbreviation;
}

enum SubmitOption {
    NONE = 'NONE',
    SUBMIT = 'SUBMIT',
    FLY = 'FLY'
}

interface LocationBearings {
    notams: LocationBearing[];
    lotams: LocationBearing[];
    aerodromes: LocationBearing[];
    locationCentre: GeoJSON.Point;
    locationArea: number;
}

export interface AdditionalAuthorityNameIdWithRequiresApproval extends NameId {
    requiresApproval: boolean;
    jurisdictionId: number | null;
    authority: SimpleAuthorityDto;
}

const minimumMissionStatusForAirspaceAuthorisationByType = {
    [AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION]:
        CombinedMissionStatus.SUBMITTED,
    [AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE]:
        CombinedMissionStatus.DRAFT,
    [AirspaceAuthorisationDto.AuthorisationType.USA_LAANC]:
        CombinedMissionStatus.DRAFT
};

/**
 * When opened as a new mission the `mission` input is `null`, but when it
 * is a cloned mission it is initialised as a value generated from `MissionService.cloneMission()`.
 *
 * The mapping to ContactDto is
 * ContactDto.identifier = aerodrome code = InfoPillData.identifier
 * ContactDto.name = aerodrome name = part of InfoPillData.name
 * ContactDto.subtype = Frequency abbreviation or Contact type = InfoPillData.label
 */
@Component({
    selector: 'mission-edit-v2-dialogue',
    templateUrl: './mission-edit-v2-dialogue.component.html',
    styleUrls: ['../styles.scss'],
    providers: [
        ExclusiveControlService,
        MissionEditService,
        AirspaceCheckService,
        FlyFreelyAuthorisationProvider,
        ExternalAuthorisationProvider,
        AirspaceAuthorisationManager,
        WorkTracker,
        {
            provide: MISSION_EDIT_INPUTS,
            useExisting: MissionEditService
        },
        WorkgroupsDataService,
        MissionNoticesDataService,
        SitesDataService,
        OperationsSearchDataService,
        ExternalMissionsDataService
    ]
})
export class MissionEditV2Dialogue implements OnInit, OnDestroy {
    @Input() mission: DisplayableMissionDto;
    @Input() sourceMissionId: number;
    @Input() scrollToSectionOnLoad?: string;

    organisation: Organisation;

    lockedFields: LockedMissionFields;
    lockEditFields = false;

    // This holds the last saved version of the mission
    savedMission: DisplayableMissionDto;

    // This holds the current resolved values for the values in the FormGroup
    missionDetails = {
        missionWorkflowVersion: null as InUseMissionWorkflowVersionDto
    };

    // A flag to indicate not to run the workflow change functions when true
    skipLoadingWorkflows = false;

    working = false;

    disabled: boolean;

    private ngUnsubscribe$ = new Subject<void>();

    rpas: SelectableRpa[];
    rpicRoles: MissionRoleDto[];
    crewRoles: MissionRoleDto[];
    missionTypes: MissionOperationTypeDto[];
    equipment: EquipmentDto[];
    rpaTypes: RpaTypeDto[];

    primaryAuthorityPilotRegisterDisabled = true;
    primaryAuthorityRpaRegisterDisabled = true;
    authoritiesWithPilotRegistersEnabled = new BehaviorSubject<number[]>([]);
    authoritiesWithRpaRegistersEnabled: number[] = [];
    selectedAuthorityIds: number[] = [];

    timeZones: string[];
    timeZoneOffsets: { [timeZone: string]: string };
    utc: string;

    missionStartTime: string;
    missionEndTime: string;

    isCreated: boolean;
    hasClonedFormResponses = false;
    isAddingContacts: boolean;
    isAddingFrequencies: boolean;
    isAddingAerodromes: boolean;
    defaultAerodromeCode: string = null;
    uploadMessage: string;
    confirmedStep: any[];
    locked: boolean;

    weather: any;
    attachments: CurrentAttachmentVersionDto[];
    missionWorkflows: InUseMissionWorkflowVersionDto[];
    location: LocationDetailsDto;

    locationFeatures$: Observable<FeatureGroup[]>;

    locationBearings: LocationBearings;

    sunriseSunset: SunriseSunset;
    sunrise: string;
    sunset: string;
    civilTwilightStart: string;
    civilTwilightEnd: string;

    currentLocalTime: string;
    newClock$ = new Subject<void>();

    uploadError: string;

    /**
     * A list of all the current mission approvals across the workflow authorities
     * as well as additional authorities added to the mission.
     */
    missionApprovals: MissionApprovalWithAuthority[] = [];
    activeApprovals: MissionApprovalWithAuthority[] = [];
    getSimplifiedAuthorityFromApproval = getSimplifiedAuthorityFromApproval;

    missionWorkflowPrimaryAuthority: OrganisationAuthorityDto;
    /**
     * List of all authorities associated to the mission approvals.
     */
    missionAuthorities: OrganisationAuthorityDto[] = [];
    missionAuthorityGroups: OrganisationAuthorityGroup[] = [];
    authorityRegistersSetUp: boolean;
    authorityRegistersActionMessage: string;
    selectRpic = true;
    showRpicRegisterDetails = false;
    /** Tracks the last RPIC-auto-populated pointOfContact value to detect user overrides */
    private lastRpicPointOfContact: string = null;
    /** Tracks the last RPIC-auto-populated pointOfContactInformation value to detect user overrides */
    private lastRpicPointOfContactInformation: string = null;
    registerEntriesOnMissionLoading = false;
    authorityIndicatorLookup: { [authorityId: number]: string };

    availableAuthorityLookup: {
        [authorityId: number]: AdditionalAuthorityNameIdWithRequiresApproval;
    };
    availableAdditionalAuthorities: AdditionalAuthorityNameIdWithRequiresApproval[] =
        [];
    allAdditionalAuthorities: AdditionalAuthorityNameIdWithRequiresApproval[] =
        [];
    allAdditionalAuthoritiesLookup: {
        [authorityId: number]: SimpleAuthorityDto;
    };
    newAdditionalAuthorityId: number;

    /**
     * Is an approval required to be created
     */
    // approvalCreationRequired = false;
    submitOption: SubmitOption = SubmitOption.NONE;
    canSubmitForApproval: boolean;
    canEditMissionCrew: boolean;
    canUseAdditionalAuthorities: boolean;
    useLegacyApprovals: boolean;
    canUseAerodromes: boolean;
    canUseEquipment: boolean;
    canUseNotams: boolean;
    canUseLotams: boolean;
    /**
     * When the user is a first class sites user. Indirect (sharing) sites
     * will work regardless.
     */
    hasSites: boolean;
    canUseWorkgroup: boolean;
    canUseProjects: boolean;
    canAddEquipment: boolean;
    canAddCraft: boolean;

    hasRpaResourceAssociation: boolean;
    canCheckAirspace: boolean;
    userCanViewAirspaceAuthorisation = false;
    userCanRequestAirspaceAuthorisation = false;

    latestAuthorisations: AirspaceAuthorisationDto[];
    latestAuthorisationStatus: AirspaceAuthorisationDto.Status;
    authorisationType: AirspaceAuthorisationDto.AuthorisationType | null = null;
    airspaceAuthorisationsEnabled: boolean;
    checkingAuthorisation: boolean;
    enabledAuthorisationTypes: AirspaceAuthorisationDto.AuthorisationType[] = [
        'NZL_AIRSHARE',
        'USA_LAANC',
        'AUS_CASA_AUTHORISATION'
    ];
    areAuthorisationRequirementsComplete: boolean;
    airspaceCheckCanFly: boolean;
    isAuthorisationOptional = true;
    showInlineAuthorisation = false;
    hasJurisdiction = false;

    isUsingAerodromes = false;
    isAddingNotams = false;
    showAirspaceDisclaimer = false;
    shouldShowAirspaceDisclaimer$ = new Subject<void>();
    airspaceDisclaimerDates: { [key: string]: Date } = {};

    currentStep: string;
    attachmentsHandler: AttachmentHandler;
    isEditingAttachments = false;

    isShowingSelectPrefillMission = false;

    workgroup$: typeof WorkgroupsDataService.prototype.workgroup$;
    projectsSource = new BehaviorSubject<ProjectDto[]>([]);
    projects$ = this.projectsSource.asObservable();

    canUseObjectives: boolean;
    missionObjectives: MissionObjectiveDto[];

    loadingWorkflow = false;

    renderForm = false;
    isActive: { [key: string]: boolean };

    isSubmittable = false;
    isValidAuthorisation = false;
    authorisationTooltip = 'Request Authorisation';

    combinedStatus: CombinedMissionStatus;

    cancelStack: (() => void)[] = [];

    personSearch = personSearch.bind(this);

    isResourcesCompleted = false;
    time: string;

    mapData$ = new BehaviorSubject<MapDataInput>({});

    showHandoffPopup = true;

    prefillErrors: { [formId: number]: boolean };

    allMissionDocumentation: MissionDocumentationDto;

    @ViewChild('map', { read: ElementRef })
    mapElement: ElementRef;
    @ViewChild(MissionDocumentation) missionDocumentation: MissionDocumentation;
    documentationStorage: DocumentationStorage = {
        storeFormResponse: (
            formId: number,
            step: string,
            response: FormResponseCommand
        ) =>
            firstValueFrom(
                this.missionService.updateDocumentation(this.savedMission.id, {
                    [step]: [response]
                })
            ).then(documentation => this.setupDocumentation(documentation))
    };

    get missionForm() {
        return this.missionEditService.missionForm;
    }

    get missionObjectivesGroup() {
        return this.missionEditService.missionObjectivesGroup;
    }

    workflowSelectionValidity$: typeof MissionEditService.prototype.workflowSelectionValidity$;

    constructor(
        private modal: BsModalRef<MissionEditV2Dialogue>,
        modalOptions: ModalOptions,
        private missionService: MissionService,
        private formatRpaPipe: FormatRpaPipe,
        public workTracker: WorkTracker,
        private rpaDialoguesService: RpaDialoguesService,
        private equipmentDialoguesService: EquipmentDialoguesService,
        private personDialoguesService: PersonnelDialoguesService,
        private missionApprovalService: MissionApprovalService,
        private missionObjectivesService: MissionObjectivesService,
        public airspaceCheckService: AirspaceCheckService,
        private airspaceDialoguesService: AirspaceDialoguesService,
        private airspaceAuthorisationManager: AirspaceAuthorisationManager,
        private airspaceAuthorisationService: AirspaceAuthorisationService,
        private logging: FlyFreelyLoggingService,
        private changeDetector: ChangeDetectorRef,
        private fullScreenService: FullScreenService,
        public angulartics2: Angulartics2,
        private commonDialoguesService: CommonDialoguesService,
        @Inject(forwardRef(() => MissionDialoguesService))
        private missionDialoguesService: MissionDialoguesService,
        private preferencesService: PreferencesService,
        private exclusiveControlService: ExclusiveControlService,
        public missionEditService: MissionEditService,
        private setupGuideChecklistService: SetupGuideChecklistService,
        private authorityDialoguesService: AuthorityDialoguesService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private rpaTypesService: RpaTypesService,
        private userService: UserService,
        private operationsSummaryService: OperationsSummaryService,
        private formatAuthorityPipe: FormatAuthorityPipe,
        workgroupDataService: WorkgroupsDataService,
        private missionEditNoticesService: MissionNoticesDataService,
        private projectsService: ProjectsService,
        private dateFormatService: DateFormatService,
        private workspaceStateService: WorkspaceStateService,
        private personService: PersonService,
        private operationsSearchDataService: OperationsSearchDataService
    ) {
        this.workflowSelectionValidity$ =
            missionEditService.workflowSelectionValidity$;

        this.workgroup$ = workgroupDataService.workgroup$;

        modalOptions.closeInterceptor = () => {
            if (this.locked === true) {
                this.exclusiveControlService.requestUnlock();
                this.isAddingFrequencies = false;
                this.isAddingContacts = false;
                return Promise.reject();
            }
            if (this.cancelStack.length > 0) {
                this.cancelStack[this.cancelStack.length - 1]();
                return Promise.reject();
            }
            if (this.missionForm.dirty) {
                return this.commonDialoguesService.showConfirmationDialogue(
                    'Confirm Cancel',
                    `You have unsaved changes, are you sure you want to cancel?`,
                    'Yes',
                    () => Promise.resolve()
                );
            }
            return Promise.resolve();
        };

        this.isCreated = false;
    }

    ngOnInit() {
        // Register the enabled authorisation types and the mission ID (if an
        // existing mission is being edited).  Placing this in ngOnInit ensures
        // @Input() values are available.  For new missions the missionId will
        // be supplied later by updateMission() when the mission is first saved.
        this.airspaceAuthorisationManager.setup(
            this.enabledAuthorisationTypes,
            this.mission?.id
        );

        this.activeTab('objectives');
        this.currentStep = 'missionName';
        this.confirmedStep = [];

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.shouldShowAirspaceDisclaimer$
            .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.showAirspaceDisclaimerDialogue());

        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(take(1), takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                if (this.mission.id != null) {
                    this.refreshMission();
                } else {
                    this.setup();
                }
            });

        this.exclusiveControlService.lock$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(locked => {
                this.locked = locked;
                this.changeDetector.detectChanges();
            });

        this.timeZones = moment.tz.names();

        this.missionEditService.missionWorkflows$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(missionWorkflows => {
                let selectedWorkflowVersionId =
                    this.missionWorkflowVersionId.value;

                if (
                    selectedWorkflowVersionId == null &&
                    this.missionWorkflowVersionId.pristine &&
                    this.savedMission?.missionWorkflowVersion != null
                ) {
                    // Find the missionWorkflowVersionId for the workflow matching the same workflowId (e.g., the new version)
                    selectedWorkflowVersionId =
                        this.savedMission.missionWorkflowVersion != null
                            ? missionWorkflows.find(
                                  w =>
                                      w.workflowId ===
                                      this.savedMission.missionWorkflowVersion
                                          .workflowId
                              )?.id ?? null
                            : null;
                    this.missionWorkflowVersionId.setValue(
                        selectedWorkflowVersionId
                    );
                }

                this.missionWorkflows = missionWorkflows.filter(
                    missionWorkflow =>
                        missionWorkflow.delegatedAuthority === null ||
                        missionWorkflow.id === selectedWorkflowVersionId ||
                        !missionWorkflow.authorityArchived
                );
                this.refreshWorkflowGroupsJurisdiction();
                this.updateMissionWorkflowVersionReference();
                this.refreshControlState();
                this.refreshCurrentAuthority();
                this.setDefaultWorkflows();
                this.generateAuthorityIndicatorLookup();
            });

        this.missionEditService.loadingWorkflow$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                loadingWorkflow => (this.loadingWorkflow = loadingWorkflow)
            );
        this.missionEditService.registerEntriesOnMissionLoading$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                loading => (this.registerEntriesOnMissionLoading = loading)
            );
    }

    /**
     * Fetches the saved mission from the server so we know the data is up tod date
     * and then kicks off the mission setup flow.
     */
    private refreshMission() {
        this.missionService
            .findMission(this.mission.id, this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: mission => {
                    this.mission = mission;
                    this.setup();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error fetching mission on open, falling back to cached version: ${error.message}`
                    );
                    this.setup();
                }
            })
            .add(this.workTracker.createTracker());
    }

    /**
     * The second phase of the setup process, where we need the organisation to be resolved.
     */
    private setup() {
        // Refresh the mission whenever an inline approval action completes.
        this.missionApprovalService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                if (this.savedMission != null && this.savedMission.id != null) {
                    this.refreshSavedMission();
                }
            });

        this.missionService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                if (this.savedMission != null && this.savedMission.id != null) {
                    this.refreshSavedMission();
                }
            });

        this.refreshPermissions();
        // Determine if the current user has access to the mission organisation
        // If not set the mission to read only mode to prevent 403 errors when fetching
        // additional mission data like crew details and workflows.
        const isInMissionOrg = this.userService
            .findUsersOrganisations()
            .some(
                org =>
                    org.id === this.mission?.organisationId ||
                    org.id === this.savedMission?.organisationId ||
                    org.id === this.organisation.id
            );

        // Determine if the user can edit this mission.
        // Assuming users will be blocked from creating missions by other UI we can
        // assume they have the right permissions if this is a new mission.
        // If this is a saved mission, we can use the mission available actions to determine permissions.
        const hasPlanningPermissions =
            (this.savedMission == null && this.mission?.id == null) ||
            this.mission?.availableActions.canEdit ||
            this.savedMission?.availableActions.canEdit;

        if (isInMissionOrg && hasPlanningPermissions) {
            this.missionEditService.setOrganisationId(
                this.mission?.organisationId ??
                    this.savedMission?.organisationId ??
                    this.organisation.id
            );
        } else {
            // The user can view the mission but cannot edit it due to not being a member
            // of the mission organisation.  Set up the service in read-only mode so that
            // no data related to the mission organisation needs to be fetched.
            this.lockEditFields = true;
            this.missionEditService.setupForReadOnly(
                this.organisation.id,
                this.mission
            );
        }
        this.missionEditService.setup();

        this.setupOperationsData();

        if (!this.canUseLotams) {
            this.missionEditNoticesService.resetNotices();
        }

        if (
            this.location?.airspaceJurisdiction?.airspaceCheckSupport !==
            AirspaceJurisdictionDto.AirspaceCheckSupport.NONE
        ) {
            // Start the mission checker so we can verify the authorisation information
            this.airspaceCheckService.startMissionCheck();
        }
        this.findCanCheckAirspace();

        combineLatest([
            this.missionForm.controls.missionTypeId.statusChanges,
            this.missionForm.controls.missionWorkflowVersionId.statusChanges,
            this.missionForm.controls.craftIds.statusChanges,
            this.missionForm.controls.rpic.statusChanges
        ])
            .pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged())
            .subscribe(() => {
                this.updateIsResourcesCompleted();
            });

        combineLatest([
            this.missionForm.controls.additionalAuthorityIds.valueChanges.pipe(
                startWith(
                    this.missionForm.controls.additionalAuthorityIds.value
                )
            ),
            this.authoritiesWithPilotRegistersEnabled.asObservable()
        ])
            .pipe(
                distinctUntilChanged(deepEqual),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([additionalAuthorityIds, registerAuthorities]) => {
                this.updateAuthorityRegisterStatuses();
            });

        this.equipmentIds.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged())
            .subscribe(value => {
                this.canAddEquipment =
                    value.filter(v => v == null).length === 0;
            });

        this.craftIds.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged())
            .subscribe(value => {
                this.canAddCraft = value.filter(v => v == null).length === 0;
            });

        // When the location in the service changes take a copy
        const location$ = this.missionEditService.missionLocation$.pipe(
            takeUntil(this.ngUnsubscribe$),
            tap(location => {
                this.location = location;
                this.findCanCheckAirspace();
                this.updateAirspaceAuthorisationsEnabled();
                this.setCurrentTime();
                this.hasJurisdiction =
                    location?.airspaceJurisdiction !== null &&
                    location?.airspaceJurisdiction !== undefined;
                this.refreshWorkflowGroupsJurisdiction();
                if (this.canUseAdditionalAuthorities) {
                    this.refreshAdditionalAuthoritiesJurisdiction();
                }
            })
        );

        const aerodromes$ =
            this.missionForm.controls.aerodromesOfInterest.valueChanges.pipe(
                distinctUntilChanged()
            );

        const notams$ = this.missionForm.controls.notams.valueChanges.pipe(
            distinctUntilChanged()
        );

        const lotams$ = this.missionForm.controls.lotams.valueChanges.pipe(
            distinctUntilChanged()
        );

        // Find all the features for the map
        this.locationFeatures$ = combineLatest([
            location$.pipe(
                map(location =>
                    location != null
                        ? getFeatureGroups(location.features, 0, 2).features
                        : []
                )
            ),
            aerodromes$.pipe(
                map(aerodromes => aerodromesToFeatureGroups(aerodromes))
            )
        ]).pipe(
            map(featureGroups =>
                featureGroups.reduce((acc, curr) => acc.concat(curr), [])
            )
        );

        notams$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(notams => {
            const existingData = this.mapData$.getValue();
            this.mapData$.next({
                ...existingData,
                NOTAM: notamToFeatureCollection(notams)
            });
        });

        combineLatest([aerodromes$, notams$, location$, lotams$])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([aerodromes, notams, _, lotams$]) => {
                this.updateGeographicInformation(aerodromes, notams, lotams$);
            });

        combineLatest([
            this.missionForm.statusChanges.pipe(distinctUntilChanged()),
            this.missionEditService.savedMission$.pipe(
                map(m => m.id),
                distinctUntilChanged()
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([valid, missionId]) => {
                this.isSubmittable = valid === 'VALID' && missionId != null;
                this.changeDetector.markForCheck();
            });

        this.missionForm.valueChanges
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                map(v => v.missionWorkflowVersionId),
                filter(v => v != null),
                distinctUntilChanged()
            )
            .subscribe(missionWorkflowVersion =>
                this.onMissionWorkflowChanged()
            );

        this.missionEditService.selectableRpas$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(rpas => {
                this.rpas = rpas;
            });

        this.missionEditService.lockedFields$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(lockedFields => (this.lockedFields = lockedFields));

        combineLatest([
            this.missionForm.controls.timeZone.valueChanges,
            this.missionForm.controls.missionDate.valueChanges
        ])
            .pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged())
            .subscribe(([timeZone, missionDate]) => {
                if (timeZone) {
                    const t = moment.tz(
                        moment(missionDate).format('YYYY-MM-DD[T]HH:mm:ss'),
                        timeZone
                    );
                    this.utc = `${t.tz('UTC').format('YYYY-MM-DD HH:mm')}Z`;
                    this.findTimeZones();
                    this.setCurrentTime();
                }
            });

        // Keep the multiple components in sync
        this.missionDate.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(date => {
                this.missionDate.setValue(date, {
                    onlySelf: true,
                    emitEvent: false,
                    emitModelToViewChange: true
                });

                this.missionStartTime = toTimestamp(
                    this.missionForm.controls.missionDate.value,
                    this.missionForm.controls.timeZone.value
                );
                const duration =
                    this.missionForm.controls.missionEstimatedTime.value;
                this.missionEndTime = moment(this.missionStartTime)
                    .add(duration ?? 3600, 'seconds')
                    .toISOString();
                this.calculateTimeOfDay();
            });

        combineLatest([
            this.missionDate.valueChanges,
            this.missionEstimatedTime.valueChanges,
            this.timeZone.valueChanges
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([date, duration, timeZone]) => {
                this.refreshNearbyMissions(date, duration, timeZone);
            });

        const doneWorking = this.workTracker.createTracker();
        this.refreshData()
            .then(() => {
                this.setupMission(this.mission, this.scrollToSectionOnLoad);
            })
            .then(() => {
                if (this.isUsingAerodromes) {
                    this.angulartics2.eventTrack.next({
                        action: 'aerodromes',
                        properties: {
                            category: 'features',
                            label: SCREEN_IDENTIFIER
                        }
                    });
                }

                doneWorking();
                // This is required to overcome a ExpressionChangedAfterItHasBeenCheckedError caused by updating the form
                this.changeDetector.detectChanges();
            });

        this.airspaceCheckService.sunriseSunset$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(sunriseSunset => this.setSunriseSunset(sunriseSunset));

        this.airspaceCheckService.result$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(checkResult => {
                if (checkResult == null) {
                    return;
                }
                if (
                    checkResult.availableAutomatedAuthorisationList != null &&
                    checkResult.availableAutomatedAuthorisationList.length >
                        0 &&
                    checkResult.ruleOutcomes.find(
                        o =>
                            o.outcome != null &&
                            o.outcome === 'AUTHORISATION_REQUIRED'
                    ) != null
                ) {
                    this.isAuthorisationOptional = false;
                } else {
                    this.isAuthorisationOptional = true;
                }
                this.showInlineAuthorisation =
                    checkResult.availableAutomatedAuthorisationList.includes(
                        AirspaceCheckDto.AvailableAutomatedAuthorisationList
                            .USA_LAANC
                    ) && checkResult.jurisdiction === 'USA';
                if (
                    this.showInlineAuthorisation &&
                    this.missionForm.controls.visualLineOfSight.value !==
                        DisplayableMissionDto.VisualLineOfSight.VLOS
                ) {
                    this.missionForm.patchValue({
                        visualLineOfSight:
                            DisplayableMissionDto.VisualLineOfSight.VLOS
                    });
                    this.missionForm.controls.visualLineOfSight.markAsDirty();
                }
                this.refreshControlState();
                this.changeDetector.markForCheck();
            });

        observeFormControl(this.missionObjectivesGroup.controls.workgroup)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((workgroup: WorkGroup) => {
                if (workgroup != null) {
                    if (
                        this.savedMission?.maximumHeight == null &&
                        workgroup.defaultMaximumHeight != null
                    ) {
                        this.missionForm.controls.maximumHeight.patchValue(
                            workgroup.defaultMaximumHeight,
                            { emitEvent: false }
                        );
                    }
                    if (
                        this.savedMission?.missionType == null &&
                        workgroup.defaultOperationType != null
                    ) {
                        this.missionForm.controls.missionTypeId.patchValue(
                            workgroup.defaultOperationType.id,
                            { emitEvent: false }
                        );
                    }
                }
                this.setDefaultWorkflows();
            });

        this.refreshAirspacePermissions();
        this.setupRpicPointOfContactAutoFill();

        this.airspaceAuthorisationService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                // if the mission is null it's a draft mission and can't be fetched without being saved first
                if (this.savedMission != null && this.savedMission.id != null) {
                    this.refreshSavedMission();
                }
            });

        this.airspaceAuthorisationManager.missionAirspaceAuthorisations$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(authorisation => {
                this.latestAuthorisations = authorisation;
                this.latestAuthorisationStatus =
                    calculateAuthorisationListState(
                        authorisation
                    ) as AirspaceAuthorisationDto.Status;
                this.setupAirspaceAuthorisationResult();
                this.changeDetector.detectChanges();
            });

        this.airspaceAuthorisationManager.checkingAuthorisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(checking => (this.checkingAuthorisation = checking));

        this.initialiseProjects();
    }

    private refreshWorkflowGroupsJurisdiction() {
        if (this.missionWorkflows == null) {
            return;
        }
        this.missionWorkflows = this.missionWorkflows
            .map(item => ({
                ...item,
                disabled: item.authorityArchived,
                // group the workflows by the mission's jurisdiction
                ...(this.location &&
                item.delegatedAuthority !== null &&
                item.delegatedAuthority?.authorityType?.jurisdiction?.id
                    ? this.location.airspaceJurisdiction !== null &&
                      this.location.airspaceJurisdiction.id ===
                          item.delegatedAuthority.authorityType.jurisdiction.id
                        ? { jurisdiction: 'This Jurisdiction' }
                        : { jurisdiction: 'Other Jurisdictions' }
                    : {
                          jurisdiction: 'Workflows With No Jurisdiction'
                      })
            }))
            .sort((a, b) => {
                const order = {
                    'This Jurisdiction': 0,
                    'Other Jurisdictions': 1,
                    'Workflows With No Jurisdiction': 2
                };
                return order[a.jurisdiction] - order[b.jurisdiction];
            });
    }

    ngOnDestroy() {
        this.newClock$.next();
        this.newClock$.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.mapData$.complete();
        this.authoritiesWithPilotRegistersEnabled.complete();
        this.shouldShowAirspaceDisclaimer$.complete();
    }

    private initialiseProjects() {
        if (this.canUseProjects) {
            this.projectsService
                .find(this.organisation.id, false)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: projects => {
                        this.projectsSource.next(projects);
                    },
                    error: error => {
                        this.logging.error(
                            error,
                            `Error while fetching available projects: ${error.message}`
                        );
                    }
                });
        }
    }

    setupFormArrays() {
        // Reset form arrays since data may have changed
        while (this.missionForm.controls.aerodromesOfInterest.length > 0) {
            this.missionForm.controls.aerodromesOfInterest.removeAt(0);
        }
        while (this.missionForm.controls.missionEmergencyContacts.length > 0) {
            this.missionForm.controls.missionEmergencyContacts.removeAt(0);
        }
        while (this.missionForm.controls.missionRadioFrequencies.length > 0) {
            this.missionForm.controls.missionRadioFrequencies.removeAt(0);
        }
        while (this.missionForm.controls.notams.length > 0) {
            this.missionForm.controls.notams.removeAt(0);
        }
        while (this.missionForm.controls.lotams.length > 0) {
            this.missionForm.controls.lotams.removeAt(0);
        }

        if (this.savedMission.aerodromesOfInterest?.length) {
            this.savedMission.aerodromesOfInterest.forEach(airport => {
                this.missionForm.controls.aerodromesOfInterest.push(
                    new FormControl(airport)
                );
            });
        }
        if (this.savedMission.missionContacts?.length) {
            this.savedMission.missionContacts.forEach(contact => {
                this.missionForm.controls.missionEmergencyContacts.push(
                    new FormControl(contact)
                );
            });
        }

        if (this.savedMission.missionRadioFrequencies?.length) {
            this.savedMission.missionRadioFrequencies.forEach(frequency => {
                this.missionForm.controls.missionRadioFrequencies.push(
                    new FormControl(frequency)
                );
            });
        }

        if (this.savedMission.notams?.length > 0) {
            this.savedMission.notams.forEach(notam => {
                this.missionForm.controls.notams.push(new FormControl(notam));
            });
        }

        if (this.savedMission.missionNoticeList?.length > 0) {
            this.savedMission.missionNoticeList
                .filter(
                    lotam =>
                        lotam.status === MissionNoticeDto.Status.APPLICABLE ||
                        lotam.status ===
                            MissionNoticeDto.Status.INFORMATIONAL ||
                        lotam.status === MissionNoticeDto.Status.BLOCK
                )
                .sort(missionNoticeSort)
                .forEach(lotam => {
                    this.missionForm.controls.lotams.push(
                        new FormControl(lotam)
                    );
                });
        }
    }

    private refreshPermissions() {
        this.canSubmitForApproval = !this.organisation.personalOrganisation;
        this.canEditMissionCrew = !this.organisation.personalOrganisation;

        this.canUseAdditionalAuthorities = hasFeatureFlag(
            this.organisation,
            FEATURE_ADDITIONAL_AUTHORITIES
        );
        this.canUseObjectives = hasFeatureFlag(
            this.organisation,
            FEATURE_MISSION_OBJECTIVES
        );

        this.canUseAerodromes = hasFeatureFlag(
            this.organisation,
            FEATURE_MISSION_AERODROMES
        );

        this.canUseEquipment = hasFeatureFlag(
            this.organisation,
            FEATURE_EQUIPMENT
        );

        this.hasRpaResourceAssociation = hasFeatureFlag(
            this.organisation,
            FEATURE_RPA_RESOURCE_ASSOCIATION
        );

        this.canUseNotams = hasFeatureFlag(this.organisation, FEATURE_NOTAMS);
        this.canUseLotams = hasFeatureFlag(this.organisation, FEATURE_LOTAM);
        this.hasSites = hasFeatureFlag(this.organisation, FEATURE_SITES);

        this.canUseWorkgroup = hasFeatureFlag(
            this.organisation,
            FEATURE_WORKGROUPS
        );

        this.canUseProjects = hasFeatureFlag(
            this.organisation,
            FEATURE_PROJECTS
        );

        this.useLegacyApprovals = hasFeatureFlag(
            this.organisation,
            FEATURE_LEGACY_MISSION_APPROVALS
        );
    }

    refreshAirspacePermissions() {
        // Check if the current user can use airspace authorisations, eg. LAANC requiring the current user to be an RPIC on the mission
        combineLatest([
            this.airspaceCheckService.completeCheck$,
            this.missionForm.controls.rpic.valueChanges.pipe(
                startWith(this.missionForm.controls.rpic.value)
            ),
            this.airspaceAuthorisationManager.missionAirspaceAuthorisations$.pipe(
                startWith(null)
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([completedCheck, rpic, authorisations]) => {
                this.authorisationType =
                    completedCheck.result
                        ?.availableAutomatedAuthorisationList != null
                        ? completedCheck.result
                              .availableAutomatedAuthorisationList[0]
                        : null;
                // Check if the current user has the correct feature flags to use authorisations
                const canDoAuthorisations =
                    this.authorisationType ===
                    AirspaceAuthorisationDto.AuthorisationType
                        .AUS_CASA_AUTHORISATION
                        ? hasFeatureFlag(
                              this.organisation,
                              FEATURE_CASA_AUTHORISATION
                          ) &&
                          hasFeatureFlag(
                              this.userService.getPersonalOrganisation(),
                              FEATURE_CASA_AUTHORISATION
                          )
                        : true;

                // Check if the current airspace jurisdiction's authorisations require that the current user be the RPIC
                if (
                    completedCheck != null &&
                    completedCheck.result != null &&
                    !completedCheck.result.availableAutomatedAuthorisationList?.includes(
                        airspaceAuthorisationTypesRequiringCurrentUserAsRpic[
                            completedCheck.result.jurisdiction
                        ]
                    )
                ) {
                    this.userCanViewAirspaceAuthorisation = canDoAuthorisations;
                    this.userCanRequestAirspaceAuthorisation =
                        canDoAuthorisations;
                    return;
                }

                // Check if the current user is an RPIC on the current mission and there are no existing authorisations
                // This implies the current user can perform a new authorisation request.
                const currentUser = this.userService.getCurrentUser();
                this.userCanRequestAirspaceAuthorisation =
                    canDoAuthorisations && rpic?.rpicId === currentUser.id;

                // This assumes that any authorisations returned are for this person, but this logic can be changed here at a later date
                this.userCanViewAirspaceAuthorisation =
                    canDoAuthorisations &&
                    authorisations != null &&
                    authorisations.length > 0;
            });
    }

    /**
     * Watch RPIC changes and auto-populate pointOfContact/pointOfContactInformation
     * when those fields are blank or still match the previously auto-populated RPIC values.
     */
    private setupRpicPointOfContactAutoFill() {
        this.missionForm.controls.rpic.valueChanges
            .pipe(
                startWith(this.missionForm.controls.rpic.value),
                pairwise(),
                switchMap(([_oldRpic, newRpic]) => {
                    if (newRpic?.rpicId == null) {
                        return of(null);
                    }
                    return this.personService
                        .findPerson(newRpic.rpicId, this.organisation.id)
                        .pipe(catchError(() => of(null)));
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(person => {
                this.applyRpicPointOfContactAutoFill(person, false);
            });
    }

    /**
     * Apply RPIC person details to the point of contact fields.
     * When `force` is false, only updates both fields together if both are
     * either empty or still match the previously auto-populated RPIC values —
     * if either field has been manually changed, neither is updated.
     * When `force` is true, both fields are always overwritten.
     */
    private applyRpicPointOfContactAutoFill(
        person: {
            firstName?: string;
            lastName?: string;
            phoneNumber?: string;
        } | null,
        force: boolean
    ) {
        const currentPoc = this.missionForm.controls.pointOfContact?.value;
        const currentPocInfo =
            this.missionForm.controls.pointOfContactInformation?.value;

        const pocIsEmpty = currentPoc == null || currentPoc.trim() === '';
        const pocMatchesLastRpic = currentPoc === this.lastRpicPointOfContact;
        const pocInfoIsEmpty =
            currentPocInfo == null || currentPocInfo.trim() === '';
        const pocInfoMatchesLastRpic =
            currentPocInfo === this.lastRpicPointOfContactInformation;

        // Both fields must be eligible for update — they relate to each other
        const canAutoFill =
            force ||
            ((pocIsEmpty || pocMatchesLastRpic) &&
                (pocInfoIsEmpty || pocInfoMatchesLastRpic));

        if (!canAutoFill) {
            return;
        }

        if (person != null) {
            const newPoc =
                `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() ||
                null;
            const newPocInfo = person.phoneNumber ?? null;

            this.missionForm.controls.pointOfContact?.patchValue(newPoc);
            this.missionForm.controls.pointOfContactInformation?.patchValue(
                newPocInfo
            );
            this.lastRpicPointOfContact = newPoc;
            this.lastRpicPointOfContactInformation = newPocInfo;
        } else {
            // RPIC cleared or lookup failed — clear both auto-filled values
            this.missionForm.controls.pointOfContact?.patchValue(null);
            this.missionForm.controls.pointOfContactInformation?.patchValue(
                null
            );
            this.lastRpicPointOfContact = null;
            this.lastRpicPointOfContactInformation = null;
        }
    }

    /**
     * Forcibly copy the current RPIC's name and phone number into the point
     * of contact fields, regardless of any manual edits.
     */
    useRpicAsPointOfContact() {
        const rpicId = this.missionForm.controls.rpic?.value?.rpicId;
        if (rpicId == null) {
            return;
        }
        this.personService
            .findPerson(rpicId, this.organisation.id)
            .pipe(
                take(1),
                catchError(() => of(null))
            )
            .subscribe(person =>
                this.applyRpicPointOfContactAutoFill(person, true)
            );
    }

    /**
     * Set the flags for the airspace checker, and start it if required.
     * This needs to happen after the MissionEditService is setup.
     */
    private findCanCheckAirspace() {
        // Turn on the full check if required
        if (
            this.location?.airspaceJurisdiction?.airspaceCheckSupport ===
            AirspaceJurisdictionDto.AirspaceCheckSupport.AIRSPACE_CHECK
        ) {
            this.canCheckAirspace = true;
            this.showAirspaceDisclaimer = false;
            this.shouldShowAirspaceDisclaimer$.next();
        } else {
            this.canCheckAirspace = false;
        }
    }

    private updateAirspaceAuthorisationsEnabled() {
        // This is updated here on each check in case of location changes
        const authorisationSupport =
            this.location?.airspaceJurisdiction?.airspaceCheckSupport;
        this.airspaceAuthorisationsEnabled =
            authorisationSupport != null &&
            authorisationSupport !==
                AirspaceJurisdictionDto.AirspaceCheckSupport.NONE;
    }

    get canViewAuthorisationsInCurrentMissionStatus() {
        if (this.authorisationType == null) {
            return false;
        }
        const requiredMissionStatus =
            minimumMissionStatusForAirspaceAuthorisationByType[
                this.authorisationType
            ];
        return (
            requiredMissionStatus === CombinedMissionStatus.DRAFT ||
            this.combinedStatus === CombinedMissionStatus.SUBMITTED
        );
    }

    private setupOperationsData() {
        const findDateRange = (): DateRange => {
            const { missionDate, timeZone, missionEstimatedTime } =
                this.missionForm.value;
            return {
                start: moment(missionDate)
                    .tz(
                        timeZone ??
                            this.organisation.timeZone ??
                            defaultTimezone
                    )
                    .startOf('day')
                    .toISOString(),
                end: moment(missionDate)
                    .tz(
                        timeZone ??
                            this.organisation.timeZone ??
                            defaultTimezone
                    )
                    .add(missionEstimatedTime, 'seconds')
                    .endOf('day')
                    .toISOString()
            };
        };

        observeFormControl(this.missionForm.controls.missionDate)
            .pipe(
                debounceTime(500),
                distinctUntilChanged(deepEqual),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(date =>
                this.operationsSearchDataService.find(
                    findDateRange(),
                    this.organisation.id
                )
            );
    }

    refreshNearbyMissions(date: Date, duration: number, timeZone: string) {
        const startTime = moment.tz(date, timeZone).startOf('day');
        const endTime = moment(startTime).endOf('day');
        this.operationsSummaryService
            .findMissionLocations({
                organisationId: this.organisation.id,
                startTime: startTime.toISOString(),
                endTime: endTime.toISOString()
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(missionLocationList => {
                const [pointLocations, polygonLocations] =
                    missionLocationsToFeatureCollection(
                        missionLocationList.map(missionLocation => ({
                            ...missionLocation,
                            // Filter the current mission from the locations, but not the current location
                            // This means that overlapping missions using the same location ID will still show if their times overlap.
                            missionList: missionLocation.missionList.filter(
                                mission => this.savedMission.id !== mission.id
                            )
                        })),
                        this.dateFormatService
                    );
                this.mapData$.next({
                    ...this.mapData$.getValue(),
                    MISSIONS: polygonLocations,
                    MISSION_MARKERS: pointLocations
                });
            });
    }

    private updateAuthorityRegisterStatuses() {
        const missionWorkflow = this.savedMission?.missionWorkflowVersion;
        const additionalAuthorityIds =
            this.missionForm.controls.additionalAuthorityIds.value;

        this.selectedAuthorityIds =
            missionWorkflow != null
                ? [missionWorkflow.delegatedAuthorityId].concat(
                      missionWorkflow.additionalAuthorityIds
                  )
                : [];

        additionalAuthorityIds?.forEach(authorityId => {
            this.selectedAuthorityIds.push(authorityId);
        });
    }

    findTimeZones() {
        const date = this.missionForm.controls.missionDate.value;
        if (date != null) {
            this.timeZoneOffsets = this.timeZones.reduce((acc, tz) => {
                const offset = moment
                    .tz(moment(date).format('YYYY-MM-DD[T]HH:mm:ss'), tz)
                    .format('Z');
                return {
                    ...acc,
                    [tz]: offset
                };
            }, {});
        }
    }

    setCurrentTime() {
        this.newClock$.next();
        this.currentLocalTime = '';
        const timeZone = this.lockedFields?.timeZone
            ? this.location?.timeZone
            : this.timeZone.value;
        if (timeZone != null) {
            timer(0, 1000)
                .pipe(
                    map(() =>
                        toLocalTime(
                            new Date(),
                            timeZone,
                            this.dateFormatService.getTimeFormat()
                        )
                    ),
                    takeUntil(this.newClock$),
                    takeUntil(this.ngUnsubscribe$)
                )
                .subscribe(time => (this.currentLocalTime = time));
        }
    }

    private refreshSavedMission(scrollToAfterRefresh?: string) {
        this.missionService
            .findMission(this.savedMission.id, this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: mission =>
                    this.setupMission(mission, scrollToAfterRefresh),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error refreshing mission for authorisation check: ${error.message}`
                    );
                    this.changeDetector.detectChanges();
                }
            })
            .add(this.workTracker.createTracker());
    }

    /**
     * Await all dependent data from the service.
     */
    private refreshData() {
        const today = moment().format('YYYY-MM-DD');

        // Authority groups must be awaited before setupMission() runs because
        // updateMissionApprovals() reads this.missionAuthorityGroups to resolve
        // the authorityType for each approval.
        const authorityGroupsPromise = firstValueFrom(
            combineLatest([
                this.organisationAuthorityService.findAuthorities(
                    this.organisation.id,
                    this.mission?.organisationId ?? this.organisation.id
                ),
                this.organisationAuthorityService.findSharedAuthorities(
                    this.organisation.id,
                    today
                )
            ]).pipe(
                take(1),
                map(([authorities, sharedAuthorityGroups]) => {
                    const additionalAuthorities = authorities
                        .filter(
                            group =>
                                !group.isPrimary &&
                                group.hasWorkflow &&
                                !isDiscontinued(group)
                        )
                        .map(group =>
                            group.authorities
                                .filter(a => !a.archived && !a.expired)
                                .map(auth => {
                                    const simpleAuthority = toSimpleAuthority(
                                        auth,
                                        this.organisation,
                                        group
                                    );
                                    return {
                                        id: auth.id,
                                        name: this.formatAuthorityPipe.transform(
                                            simpleAuthority
                                        ),
                                        authority: simpleAuthority,
                                        requiresApproval:
                                            simpleAuthority.requiresApproval,
                                        jurisdictionId:
                                            group.jurisdiction?.id ?? null
                                    };
                                })
                        )
                        .reduce((acc, auths) => [...acc, ...auths], []);

                    const sharedAuthorities = sharedAuthorityGroups
                        .filter(
                            group =>
                                !group.isPrimary &&
                                group.hasWorkflow &&
                                !isDiscontinued(group)
                        )
                        .map(group =>
                            group.authorities
                                .filter(a => !a.archived && !a.expired)
                                .map(auth => {
                                    const simpleAuthority = toSimpleAuthority(
                                        auth,
                                        auth.organisation,
                                        group
                                    );
                                    return {
                                        id: auth.id,
                                        name: this.formatAuthorityPipe.transform(
                                            simpleAuthority
                                        ),
                                        authority: simpleAuthority,
                                        requiresApproval:
                                            simpleAuthority.requiresApproval,
                                        jurisdictionId:
                                            group.jurisdiction?.id ?? null
                                    };
                                })
                        )
                        .reduce((acc, auths) => [...acc, ...auths], []);

                    this.availableAdditionalAuthorities = [
                        ...additionalAuthorities,
                        ...sharedAuthorities
                    ];
                    this.refreshAdditionalAuthoritiesJurisdiction();

                    this.allAdditionalAuthorities =
                        this.availableAdditionalAuthorities;

                    // Build a list of authority IDs where the registers are enabled.
                    // Also remove any IDs from the list for authorities where the registers are disabled.
                    const authoritiesWithPilotRegisters =
                        this.authoritiesWithPilotRegistersEnabled.getValue();
                    const authoritiesWithRpaRegisters =
                        this.authoritiesWithRpaRegistersEnabled;

                    const addOrRemoveRegisterFromList = (
                        register: AuthorityRegisterSummaryDto,
                        enabledList: number[],
                        authorityId: number
                    ) => {
                        if (
                            register != null &&
                            register.registerMode !==
                                AuthorityRegisterSummaryDto.RegisterMode
                                    .DISABLED &&
                            !enabledList.includes(authorityId)
                        ) {
                            enabledList.push(authorityId);
                        } else if (
                            (register == null ||
                                register.registerMode ===
                                    AuthorityRegisterSummaryDto.RegisterMode
                                        .DISABLED) &&
                            enabledList.includes(authorityId)
                        ) {
                            const index = enabledList.findIndex(
                                id => id === authorityId
                            );
                            enabledList.splice(index, 1);
                        }
                    };

                    authorities.concat(sharedAuthorityGroups).forEach(group =>
                        group.authorities.forEach(authority => {
                            const pilotRegister = authority.registers.find(
                                reg =>
                                    reg.registerEntityType ===
                                    AuthorityRegisterSummaryDto
                                        .RegisterEntityType.PILOT
                            );
                            const rpaRegister = authority.registers.find(
                                reg =>
                                    reg.registerEntityType ===
                                    AuthorityRegisterSummaryDto
                                        .RegisterEntityType.RPA
                            );

                            addOrRemoveRegisterFromList(
                                pilotRegister,
                                authoritiesWithPilotRegisters,
                                authority.id
                            );
                            addOrRemoveRegisterFromList(
                                rpaRegister,
                                authoritiesWithRpaRegisters,
                                authority.id
                            );
                        })
                    );
                    this.authoritiesWithPilotRegistersEnabled.next(
                        authoritiesWithPilotRegisters
                    );
                    this.authoritiesWithRpaRegistersEnabled =
                        authoritiesWithRpaRegisters;

                    this.missionAuthorityGroups = authorities.concat(
                        sharedAuthorityGroups
                    );
                    this.allAdditionalAuthoritiesLookup = {};
                    [...authorities, ...sharedAuthorityGroups].forEach(group =>
                        group.authorities.forEach(auth => {
                            this.allAdditionalAuthoritiesLookup[auth.id] = {
                                archived: auth.archived,
                                authorityType: group,
                                expired: auth.expired,
                                hasLibrary: null,
                                id: auth.id,
                                organisation: null,
                                requiresApproval: auth.requiresApproval,
                                requiresApprovalPolicy:
                                    auth.requiresApprovalPolicy,
                                canRpicFinalise: auth.canRpicFinalise,
                                startDate: auth.startDate,
                                status: auth.status,
                                workflowStatus: auth.workflowStatus
                            };
                        })
                    );

                    return this.availableAdditionalAuthorities;
                }),
                tap(additionalAuthorities => {
                    this.availableAuthorityLookup =
                        additionalAuthorities.reduce(toLookup, {});
                }),
                takeUntil(this.ngUnsubscribe$)
            )
        );

        return Promise.all([
            authorityGroupsPromise,
            firstValueFrom(this.missionEditService.locations$),

            firstValueFrom(this.missionEditService.rpas$),

            firstValueFrom(
                this.preferencesService
                    .findPreferencesAsOption(MISSION_FIELD_APP_INFO, null)
                    .pipe(
                        takeUntil(this.ngUnsubscribe$),
                        tap(preferences => {
                            this.showHandoffPopup = getOrElse(() => ({
                                show: true
                            }))(preferences).show;
                        })
                    )
            ).catch((error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error while setting up mission documentation: ${error.message}`
                );
            }),

            firstValueFrom(
                this.missionEditService.missionRoles$.pipe(
                    takeUntil(this.ngUnsubscribe$),
                    take(1),
                    tap(roles => {
                        this.crewRoles = roles.filter(
                            r =>
                                r.coreRole !==
                                MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                        );
                        this.rpicRoles = roles.filter(
                            r =>
                                r.coreRole ===
                                MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                        );
                    })
                )
            ),

            firstValueFrom(
                this.missionObjectivesService.find(this.organisation.id, false)
            ).then(
                missionObjectives =>
                    (this.missionObjectives = missionObjectives),
                (error: FlyFreelyError) => {
                    this.logging.error(error);
                }
            ),

            firstValueFrom(
                this.rpaTypesService.findRpaTypes(this.organisation.id).pipe(
                    takeUntil(this.ngUnsubscribe$),
                    tap(types => {
                        this.rpaTypes = types;
                    })
                )
            )
        ]);
    }

    private refreshAdditionalAuthoritiesJurisdiction() {
        if (this.availableAdditionalAuthorities == null) {
            return;
        }
        this.availableAdditionalAuthorities =
            this.availableAdditionalAuthorities
                .map(item => ({
                    ...item,
                    // group the authority by the mission's jurisdiction
                    ...(this.location && item.jurisdictionId !== null
                        ? this.location.airspaceJurisdiction !== null &&
                          this.location.airspaceJurisdiction.id ===
                              item.jurisdictionId
                            ? {
                                  jurisdiction: 'This Jurisdiction'
                              }
                            : {
                                  jurisdiction: 'Other Jurisdictions'
                              }
                        : {
                              jurisdiction: 'Authorities With No Jurisdiction'
                          })
                }))
                .sort((a, b) => {
                    const order = {
                        'This Jurisdiction': 0,
                        'Other Jurisdictions': 1,
                        'Authorities With No Jurisdiction': 2
                    };
                    return order[a.jurisdiction] - order[b.jurisdiction];
                });
    }

    addCrew() {
        if (this.canAddRow()) {
            this.missionEditService.addCrew();
            this.changeDetector.markForCheck();
        }
    }

    deleteCrew(i: number) {
        this.missionEditService.removeCrew(i);
        this.changeDetector.detectChanges();
    }

    addRpa() {
        this.missionEditService.addRpa(null);
        this.changeDetector.markForCheck();
    }

    deleteRpa(i: number) {
        this.missionEditService.removeRpa(i);
        this.changeDetector.detectChanges();
    }

    addEquipment() {
        this.missionEditService.addEquipment(null);
        this.changeDetector.markForCheck();
    }

    deleteEquipment(i: number) {
        this.missionEditService.removeEquipment(i);
        this.changeDetector.detectChanges();
    }

    canAddRow() {
        return this.missionCrew.value.reduce(
            (acc: boolean, crew: MissionCrewDto) =>
                acc && crew.personId != null && crew.missionRoleId != null,
            true
        );
    }

    private setupLookups() {
        this.missionService
            .findMissionTypes(this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: missionTypes => {
                    this.missionTypes = missionTypes;
                },
                error: error => {
                    this.logging.error(error);
                }
            });
    }

    /**
     * Update the component component state with a mission from the server,
     * or the service that initiated this component (cloned missions).
     *
     * This method assumes that all data is loaded at this point.
     *
     * @param mission the server mission that will update the component state
     */
    private setupMission(
        mission: DisplayableMissionDto,
        scrollToAfterRefresh?: string
    ) {
        this.missionEditService.savedMission = mission;
        this.savedMission = mission;
        this.combinedStatus = displayableMissionDtoCombinedStatus(mission);
        if (
            this.combinedStatus !== CombinedMissionStatus.DRAFT &&
            this.combinedStatus !== CombinedMissionStatus.SUBMITTED
        ) {
            this.modal.hide();
            return;
        }

        this.airspaceAuthorisationManager.updateMission(mission);

        this.setupAttachmentsHandler();

        this.savedMission.timeZone =
            mission.timeZone ??
            mission.location?.timeZone ??
            this.location?.timeZone ??
            this.organisation.timeZone ??
            defaultTimezone;

        this.savedMission.uid = this.createUID(mission);

        this.getTime(mission.missionEstimatedTime);

        this.setupLookups();

        if (!mission.craftIds) {
            this.savedMission.craftIds = [];
        }

        this.isUsingAerodromes =
            this.canUseAerodromes && mission.modelVersion > 1;

        this.refreshMissionAirspaceAuthorisation();

        return firstValueFrom(this.updateMissionApprovals())
            .then(() => firstValueFrom(this.refreshDocumentation()))
            .then(() => this.prefillPreviousDocumentation())
            .then(() => this.initFormValues())
            .then(() => this.setupFormArrays())
            .then(() => this.refreshControlState())
            .then(() => this.updateAuthorityRegisterStatuses())
            .then(() => {
                if (scrollToAfterRefresh != null) {
                    this.scrollTo(scrollToAfterRefresh);
                }
            });
    }

    refreshMissionAirspaceAuthorisation() {
        this.refreshAirspaceAuthorisation();
    }

    setupAirspaceAuthorisationResult() {
        this.updateAuthorisationRequirementsComplete();
        this.changeDetector.detectChanges();
    }

    private updateAuthorisationRequirementsComplete() {
        this.areAuthorisationRequirementsComplete =
            this.isResourcesCompleted &&
            this.savedMission?.id != null &&
            this.missionWorkflowVersionId.value != null &&
            this.savedMission?.missionWorkflowVersionId ===
                this.missionWorkflowVersionId.value &&
            !this.locked;
    }

    private setupAttachmentsHandler() {
        if (this.savedMission.id != null && this.attachmentsHandler == null) {
            const managingOrganisationId =
                this.savedMission?.organisationId ?? this.organisation.id;

            this.attachmentsHandler = this.missionService.attachmentHandler(
                this.savedMission.id,
                managingOrganisationId
            );
            this.attachmentsHandler.destroyOn(this.ngUnsubscribe$);
        }
    }

    /**
     * Refresh the submit options, and other workflow related buttons. Should only occur once the workflows have been refreshed.
     */
    private refreshControlState() {
        // Read-only mode overrides all other editing-related state
        if (this.lockEditFields) {
            this.submitOption = SubmitOption.NONE;
            this.missionForm.updateValueAndValidity();
            this.changeDetector.detectChanges();
            return;
        }

        if (
            this.combinedStatus === CombinedMissionStatus.DRAFT ||
            // If the submitted mission has any draft approvals, they need to be submitted too
            (this.combinedStatus === CombinedMissionStatus.SUBMITTED &&
                this.savedMission.approvals.filter(
                    a => a.status === RequestersMissionApprovalDto.Status.DRAFT
                ).length > 0)
        ) {
            this.submitOption =
                this.approvalCreationRequired ||
                (this.savedMission.approvals != null &&
                    this.savedMission.approvals.filter(
                        a =>
                            a.status ===
                            RequestersMissionApprovalDto.Status.DRAFT
                    ).length > 0) ||
                (this.savedMission.additionalAuthorities != null &&
                    this.savedMission.additionalAuthorities.filter(
                        a => a.requiresApproval
                    ).length > 0)
                    ? SubmitOption.SUBMIT
                    : SubmitOption.FLY;
        } else {
            this.submitOption = SubmitOption.NONE;
        }
        this.missionForm.updateValueAndValidity();
        this.changeDetector.detectChanges();
    }

    initFormValues() {
        const craftIds = this.savedMission.craftIds;
        const missionCrew = this.organisation.personalOrganisation
            ? []
            : this.savedMission.missionCrewDetails
                  .filter(
                      c =>
                          c.role == null ||
                          this.rpicRoles.find(role => role.id === c.role.id) ==
                              null
                  )
                  .map(c => ({
                      personId: c.person != null ? c.person.id : null,
                      missionRoleId: c.role != null ? c.role.id : null
                  }));

        const rpicCrewMember = this.organisation.personalOrganisation
            ? null
            : this.savedMission.missionCrewDetails.find(
                  c =>
                      c.role != null &&
                      c.person != null &&
                      this.rpicRoles.find(role => role.id === c.role.id) != null
              );
        const rpic = this.organisation.personalOrganisation
            ? {
                  rpicId: this.organisation.ownerId,
                  rpicRoleId: this.rpicRoles[0]?.id
              }
            : rpicCrewMember != null
            ? {
                  rpicId: rpicCrewMember.person.id,
                  rpicRoleId: rpicCrewMember.role.id
              }
            : {
                  rpicId: null,
                  rpicRoleId:
                      this.rpicRoles?.length == 1 ? this.rpicRoles[0]?.id : null
              };

        this.selectRpic = rpic.rpicId == null;

        const equipmentIds = this.savedMission.equipment.map(e => e.id);

        const additionalAuthorities =
            this.savedMission.additionalAuthorities.map(a => a.id) ?? [];

        craftIds.forEach(id => {
            const craftIdArray = this.missionForm.controls.craftIds;
            if (craftIdArray.value.findIndex(c => c === id) === -1) {
                craftIdArray.push(new FormControl(id, Validators.required));
                craftIdArray.updateValueAndValidity();
            }
        });

        equipmentIds.forEach(id => {
            const equipmentIdArray = this.missionForm.controls.equipmentIds;
            if (equipmentIdArray.value.findIndex(e => e === id) === -1) {
                equipmentIdArray.push(new FormControl(id));
                equipmentIdArray.updateValueAndValidity();
            }
        });

        missionCrew.forEach(crew => {
            const missionCrewArray = this.missionForm.controls.missionCrew;
            if (
                missionCrewArray.value.findIndex(
                    c =>
                        c.personId === crew.personId &&
                        c.missionRoleId === crew.missionRoleId
                ) === -1
            ) {
                missionCrewArray.push(
                    new FormControl(crew, Validators.required)
                );
                missionCrewArray.setValidators([
                    Validators.required,
                    validateCrew(this.crewRoles)
                ]);
                missionCrewArray.updateValueAndValidity();
            }
        });

        additionalAuthorities.forEach(authority => {
            const additionalAuthorityArray =
                this.missionForm.controls.additionalAuthorityIds;
            if (
                additionalAuthorityArray.value.findIndex(
                    a => a === authority
                ) === -1
            ) {
                additionalAuthorityArray.push(new FormControl(authority));
                additionalAuthorityArray.updateValueAndValidity();
            }
        });

        const organisationSettings = (<PersonsOrganisationDto>this.organisation)
            .settings;
        const defaultOrganisationMaximumHeight =
            organisationSettings?.defaultMaximumHeight;
        const defaultOrganisationOperationTypeId =
            organisationSettings?.defaultOperationTypeId;
        const defaultWorkgroupMaximumHeight =
            this.missionObjectivesGroup.value?.workgroup?.defaultMaximumHeight;
        const defaultWorkgroupOperationTypeId =
            this.missionObjectivesGroup.value?.workgroup?.defaultOperationType
                ?.id;
        const missionTimeZone =
            this.savedMission.timeZone != null
                ? this.savedMission.timeZone
                : this.location?.timeZone ??
                  this.organisation.timeZone ??
                  defaultTimezone;

        this.changeDetector.markForCheck();

        // We don't emit events because if we do it will fire events as it sets values, not after all values are set
        this.missionForm.patchValue(
            {
                missionObjectivesGroup: {
                    name: this.savedMission.name || null,
                    projectId:
                        this.savedMission.project != null
                            ? this.savedMission.project.id
                            : null,
                    isDummy: this.savedMission.isDummy || false,
                    missionObjective:
                        this.savedMission.missionObjective || null,
                    missionObjectiveTypeId:
                        this.savedMission.missionObjectiveType != null
                            ? this.savedMission.missionObjectiveType.id
                            : null,
                    location: this.savedMission.location || null
                },
                missionTypeId:
                    this.savedMission.missionType != null
                        ? this.savedMission.missionType.id
                        : defaultWorkgroupOperationTypeId != null
                        ? defaultWorkgroupOperationTypeId
                        : defaultOrganisationOperationTypeId,
                rpic: rpic,
                crewNotes: this.savedMission.crewNotes || null,
                missionDate:
                    missionTimeZone != null
                        ? this.savedMission.missionDate
                            ? fromTimestamp(
                                  this.savedMission.missionDate,
                                  missionTimeZone
                              )
                            : moment().hours(8).minute(0).seconds(0).toDate()
                        : null,
                timeZone: missionTimeZone,
                missionEstimatedTime: this.savedMission.missionEstimatedTime,
                maximumHeight:
                    this.savedMission.maximumHeight != null
                        ? this.savedMission.maximumHeight
                        : defaultWorkgroupMaximumHeight != null
                        ? defaultWorkgroupMaximumHeight
                        : defaultOrganisationMaximumHeight,
                emergencyContacts: this.savedMission.emergencyContacts || null,
                radioFrequencies: this.savedMission.radioFrequencies || null,
                pointOfContact: this.savedMission.pointOfContact || null,
                pointOfContactInformation:
                    this.savedMission.pointOfContactInformation || null,
                visualLineOfSight: this.savedMission.visualLineOfSight || null,
                timeOfDay: this.savedMission.timeOfDay || null,
                declareNoRelevantNotam:
                    this.savedMission.noNotamDeclarationPerson != null &&
                    this.savedMission.noNotamDeclarationTime != null &&
                    this.missionForm.controls.notams.value?.length === 0
            },
            { emitEvent: false }
        );

        this.workgroup$.pipe(take(1)).subscribe(workgroup => {
            if (this.savedMission.workgroupId != null) {
                this.missionForm.controls.missionObjectivesGroup.patchValue(
                    {
                        workgroup: workgroup.find(
                            item => item.id === this.savedMission.workgroupId
                        )
                    },
                    { emitEvent: false }
                );
            }
        });

        this.updateAdditionalAuthorities();
        updateFormTreeValidity(this.missionForm);
        if (this.savedMission.id != null) {
            this.missionForm.markAsPristine();
        }
    }

    private setDefaultWorkflows() {
        if (this.savedMission?.missionWorkflowVersionId != null) {
            this.skipLoadingWorkflows = false;
            return;
        }
        const organisationSettings = (<PersonsOrganisationDto>this.organisation)
            .settings;
        const defaultWorkgroupWorkflowId = this.missionWorkflows?.find(
            wf =>
                wf.workflowId ===
                this.missionObjectivesGroup.controls.workgroup.value
                    ?.defaultMissionWorkflow?.id
        )?.id;
        const defaultOrganisationWorkflowId = this.missionWorkflows?.find(
            wf =>
                wf.workflowId === organisationSettings?.defaultMissionWorkflowId
        )?.id;

        if (
            defaultWorkgroupWorkflowId == null &&
            defaultOrganisationWorkflowId == null
        ) {
            this.skipLoadingWorkflows = false;
            return;
        }
        // Setting skipLoadingWorkflows here will just update the form values without initiating the change workflow flow
        // This prevents the mission saving prematurely and requires the user to confirm the workflow to continue
        this.skipLoadingWorkflows = true;
        this.missionForm.patchValue(
            {
                missionWorkflowVersionId:
                    defaultWorkgroupWorkflowId ?? defaultOrganisationWorkflowId
            },
            { onlySelf: true, emitEvent: false }
        );
    }

    private saveAndSetupMission(): Promise<DisplayableMissionDto> {
        const doneWorking = this.workTracker.createTracker();

        // If we have an unsaved location then save it.
        // if (this.location != null && this.location.id == null) {
        //     this.createMissionLocation(this.location);
        // }

        return this.missionEditService
            .saveMission()
            .then(savedMission => {
                const message =
                    this.savedMission.id != null ? 'updated' : 'saved';
                this.logging.success(`The mission has been ${message}`);
                return this.setupMission(savedMission).then(() => savedMission);
            })
            .finally(() => doneWorking());
    }

    /**
     * Prefill the documentation from the mission from which this mission was cloned
     */
    private prefillPreviousDocumentation() {
        if (
            // Only prefill docs for saved missions
            this.savedMission.id == null ||
            this.sourceMissionId == null ||
            !hasFeatureFlag(this.organisation, 'cloneDocumentation')
        ) {
            return Promise.resolve();
        }

        this.hasClonedFormResponses = true;
        this.prefillFromMission(this.sourceMissionId);
    }

    private prefillFromMission(missionId: number) {
        if (this.rpas == null) {
            this.rpas = [];
        }
        const documentationPromise = Promise.all([
            firstValueFrom(
                this.missionService.findMission(missionId, this.organisation.id)
            ),
            firstValueFrom(
                this.missionService.findDocumentation(
                    missionId,
                    this.organisation.id
                )
            ),
            firstValueFrom(
                this.missionService.findDocumentation(
                    this.savedMission.id,
                    this.organisation.id
                )
            )
        ]).then(
            ([mission, documentation, newDocumentation]) => {
                const oldDocumentation = buildDisplayableMissionDocumentation(
                    documentation,
                    this.activeMissionApprovals.map(
                        approval => approval.approval
                    ),
                    this.rpas,
                    this.formatRpaPipe.transform
                );
                const oldForms = oldDocumentation.formResponses;

                const newForms = buildDisplayableMissionDocumentation(
                    newDocumentation,
                    this.activeMissionApprovals.map(
                        approval => approval.approval
                    ),
                    this.rpas,
                    this.formatRpaPipe.transform
                );

                const { formResponses, prefillErrors } =
                    prefillPreviousDocumentation(
                        newForms.formResponses,
                        oldForms,
                        newForms.requiredDocumentation,
                        'pre-submit'
                    );

                this.prefillErrors = prefillErrors;

                this.missionForm.controls.documentation.setValue({
                    ...this.missionForm.controls.documentation.value,
                    formResponses
                });
            },
            (error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error while setting up mission documentation: ${error.message}`
                );
            }
        );
        return documentationPromise;
    }

    stepCompleted(step: string) {
        if (!this.confirmedStep) {
            return;
        }

        const foundStep = this.confirmedStep.find(st => st === step);
        switch (step) {
            case 'missionName':
                if (foundStep) {
                    if (this.isCreated) {
                        this.currentStep = 'missionObjective';
                    } else {
                        this.currentStep = 'createMission';
                    }
                }
                break;
            case 'missionObjective':
                if (foundStep) {
                    this.currentStep = 'missionLocation';
                }
                break;
            case 'missionLocation':
                this.currentStep = 'operationType';
                break;
            case 'operationType':
                this.currentStep = 'missionWorkflow';
                break;
            case 'missionWorkflow':
                this.currentStep = 'rpa';
                break;
            case 'rpa':
                this.currentStep = 'equipment';
                break;
            case 'equipment':
                this.currentStep = 'crew';
                break;
            case 'crew':
                if (foundStep) {
                    this.currentStep = 'crewNotes';
                }
                break;
            case 'crewNotes':
                if (foundStep) {
                    this.currentStep = 'plannedDate';
                }
                break;
            case 'plannedDate':
                if (foundStep) {
                    this.currentStep = 'plannedTime';
                }
                break;
            case 'plannedTime':
                if (foundStep) {
                    this.currentStep = 'maxHeight';
                }
                break;
            case 'maxHeight':
                if (foundStep) {
                    this.currentStep = 'contacts';
                }
                break;
            case 'contacts':
                if (foundStep) {
                    this.currentStep = 'frequencies';
                }
                break;
            case 'frequencies':
                if (foundStep) {
                    this.currentStep = 'attachments';
                }
                break;
            case 'attachments':
                if (foundStep) {
                    this.currentStep = 'missionFeasibility';
                }
                break;
            case 'missionFeasibility':
                if (foundStep) {
                    this.currentStep = 'jsa';
                }
                break;
            case 'jsa':
                if (foundStep) {
                    this.currentStep = 'missionSummary';
                }
                break;
            case 'missionSummary':
                if (foundStep) {
                    this.currentStep = 'none';
                }
                break;
        }
        this.changeDetector.detectChanges();
    }

    setStep(step: string) {
        if (step === this.currentStep) {
            return;
        }
        this.currentStep = step;
        this.changeDetector.detectChanges();
    }

    refreshDocumentation(): Observable<void> {
        if (this.savedMission.id == null) {
            // unsaved missions can't have documentation
            return of(undefined);
        }

        return this.missionService
            .findDocumentation(this.savedMission.id, this.organisation.id)
            .pipe(
                map(documentation => {
                    this.setupDocumentation(documentation);
                }),
                catchError((error: FlyFreelyError, _) => {
                    this.logging.error(
                        error,
                        `Error while refreshing mission documentation: ${error.message}`
                    );
                    // Swallow the error
                    return of(undefined);
                })
            );
    }

    private setupDocumentation(documentation: MissionDocumentationDto) {
        if (this.rpas == null) {
            this.rpas = [];
        }
        this.allMissionDocumentation = documentation;
        const newDocumentation = buildDisplayableMissionDocumentation(
            documentation,
            this.activeMissionApprovals.map(approval => approval.approval),
            this.rpas,
            this.formatRpaPipe.transform
        );

        const oldDocumentation = this.missionForm.get('documentation').value;

        this.missionForm.patchValue({
            documentation: {
                ...newDocumentation,
                ...prefillPreviousDocumentation(
                    newDocumentation.formResponses,
                    oldDocumentation != null
                        ? oldDocumentation.formResponses
                        : {},
                    newDocumentation.requiredDocumentation,
                    'pre-submit'
                )
            }
        });
    }

    addAerodrome(aerodrome: AerodromeDetailsDto) {
        const existingAerodromes =
            this.missionForm.controls.aerodromesOfInterest.value;
        if (
            existingAerodromes.findIndex(
                a => a.identifier === aerodrome.code
            ) === -1
        ) {
            const control = new FormControl({
                type: ContactDto.Type.AERODROME,
                name: aerodrome.name,
                value: aerodrome.operator,
                identifier: aerodrome.code,
                location: aerodrome.location
            });
            this.missionForm.controls.aerodromesOfInterest.push(control);
            control.markAsDirty();
        }

        this.angulartics2.eventTrack.next({
            action: 'aerodrome-add',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${aerodrome.code}`
            }
        });
    }

    addAerodromeFrequency(frequency: AerodromeFrequency) {
        const existingFrequencies =
            this.missionForm.controls.missionRadioFrequencies.value;
        if (
            existingFrequencies.findIndex(
                matchesAerodromeFrequency(frequency)
            ) === -1
        ) {
            const control = new FormControl({
                name: frequency.name,
                subtype: frequency.abbreviation || '',
                value: frequency.frequency || '',
                type: ContactDto.Type.AERODROME,
                identifier: frequency.aerodromeCode || null
            });
            this.missionForm.controls.missionRadioFrequencies.push(control);
            control.markAsDirty();
        }

        this.angulartics2.eventTrack.next({
            action: 'aerodrome-frequency-add',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${frequency.aerodromeCode}-${frequency.abbreviation}`
            }
        });
    }

    addAerodromeContact(contact: AerodromeContact) {
        const existingContacts =
            this.missionForm.controls.missionEmergencyContacts.value;
        if (
            existingContacts.findIndex(matchesAerodromeContact(contact)) === -1
        ) {
            const control = new FormControl({
                name: contact.aerodromeName,
                subtype: contact.type,
                value: contact.contact,
                identifier: contact.aerodromeCode,
                type: ContactDto.Type.AERODROME
            });
            this.missionForm.controls.missionEmergencyContacts.push(control);
            control.markAsDirty();
        }

        this.angulartics2.eventTrack.next({
            action: 'aerodrome-contact-add',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${contact.aerodromeCode}-${contact.type}`
            }
        });
    }

    addAerodromeNotam(notam: NotamDto) {
        this.missionForm.controls.notams.push(new FormControl(notam));
        this.missionForm.controls.notams.markAsDirty();
        this.missionForm.patchValue({
            // For an unsaved mission, this will set declareNoRelevantNotam to false
            declareNoRelevantNotam:
                this.savedMission.noNotamDeclarationPerson != null &&
                this.missionForm.controls.notams.value?.length === 0
        });

        this.angulartics2.eventTrack.next({
            action: 'notam-add',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${notam.location}`
            }
        });
    }

    findNearbyAirports() {
        // Find airports based on mission location and populate contacts and frequencies
    }

    addContact(item: ContactDto) {
        const control = new FormControl({
            ...item,
            type: ContactDto.Type.CUSTOM
        });
        this.missionForm.controls.missionEmergencyContacts.push(control);
        control.markAsDirty();
        this.isAddingContacts = false;
    }

    addFrequency(item: ContactDto) {
        const control = new FormControl({
            ...item,
            type: ContactDto.Type.CUSTOM
        });
        this.missionForm.controls.missionRadioFrequencies.push(control);
        control.markAsDirty();
        this.isAddingFrequencies = false;
    }

    removeAirport(index: number) {
        this.missionForm.controls.aerodromesOfInterest.removeAt(index);
        this.missionForm.controls.aerodromesOfInterest.markAsDirty();

        this.angulartics2.eventTrack.next({
            action: 'aerodrome-remove',
            properties: {
                category: SCREEN_IDENTIFIER
            }
        });
    }

    removeSearchAirport(aerodrome: AerodromeDetailsDto) {
        const aerodromes = this.missionForm.controls.aerodromesOfInterest.value;
        const index = aerodromes.findIndex(
            a => a.identifier === aerodrome.code
        );
        if (index !== -1) {
            this.missionForm.controls.aerodromesOfInterest.removeAt(index);
            this.missionForm.controls.aerodromesOfInterest.markAsDirty();
        }

        this.angulartics2.eventTrack.next({
            action: 'aerodrome-remove',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${aerodrome.code}`
            }
        });
    }

    removeContact(index: number) {
        this.missionForm.controls.missionEmergencyContacts.removeAt(index);
        this.missionForm.controls.missionEmergencyContacts.markAsDirty();

        this.angulartics2.eventTrack.next({
            action: 'contact-remove',
            properties: {
                category: SCREEN_IDENTIFIER
            }
        });
    }

    removeSearchContact(contact: AerodromeContact) {
        const contacts =
            this.missionForm.controls.missionEmergencyContacts.value;
        const index = contacts.findIndex(matchesAerodromeContact(contact));
        if (index !== -1) {
            this.missionForm.controls.missionEmergencyContacts.removeAt(index);
            this.missionForm.controls.missionEmergencyContacts.markAsDirty();
        }

        this.angulartics2.eventTrack.next({
            action: 'aerodrome-contact-remove',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${contact.aerodromeCode}-${contact.type}`
            }
        });
    }

    removeFrequency(index: number) {
        this.missionForm.controls.missionRadioFrequencies.removeAt(index);
        this.missionForm.controls.missionRadioFrequencies.markAsDirty();

        this.angulartics2.eventTrack.next({
            action: 'frequency-remove',
            properties: {
                category: SCREEN_IDENTIFIER
            }
        });
    }

    removeSearchFrequency(frequency: AerodromeFrequency) {
        const frequencies =
            this.missionForm.controls.missionRadioFrequencies.value;
        const index = frequencies.findIndex(
            matchesAerodromeFrequency(frequency)
        );
        if (index !== -1) {
            this.missionForm.controls.missionRadioFrequencies.removeAt(index);
            this.missionForm.controls.missionRadioFrequencies.markAsDirty();
        }

        this.angulartics2.eventTrack.next({
            action: 'aerodrome-frequency-remove',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${frequency.aerodromeCode}-${frequency.abbreviation}`
            }
        });
    }

    removeSearchNotam(notam: NotamDto) {
        const notams = this.missionForm.controls.notams.value;
        const index = notams.findIndex(n => n.id === notam.id);
        if (index !== -1) {
            this.missionForm.controls.notams.removeAt(index);
            this.missionForm.controls.notams.markAsDirty();
        }
        this.missionForm.patchValue({
            // For an unsaved mission, this will set declareNoRelevantNotam to false
            declareNoRelevantNotam:
                this.savedMission.noNotamDeclarationPerson != null &&
                this.missionForm.controls.notams.value?.length === 0
        });

        this.angulartics2.eventTrack.next({
            action: 'notam-remove',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${notam.location}`
            }
        });
    }

    createUID(mission: DisplayableMissionDto) {
        if (mission.uid != null) {
            return mission.uid;
        }
        // date-organisation_id-sequential_number
        const currentDate = new Date();
        const datestring = moment(currentDate).format('YYYYMMDD-HHmmss');
        const organisationID = this.organisation.id;
        const UID = `${organisationID}-${datestring}`;
        return UID;
    }

    getTime(time: number) {
        const hours = Math.floor(time / 3600);
        time = time - hours * 3600;
        const minutes = Math.floor(time / 60);
        const timeString = `${hours} hours and ${minutes} minutes`;

        return timeString;
    }

    isPlanningCompleted() {
        if (
            this.missionForm.controls.documentation.invalid ||
            this.missionForm.controls.documentation.value == null
        ) {
            return false;
        }

        const value = this.missionForm.controls.documentation.value;

        // TODO: need to verify when the forms are done, the problem is that we don't have formResponses at this stage.
        const presubmit = value.requiredDocumentation['pre-submit'];

        const presubmitCompleted = presubmit.reduce(
            (acc, requirements) =>
                acc &&
                areRequirementsCompleted(
                    requirements,
                    value.formResponses,
                    'pre-submit'
                ),
            true
        );

        return this.missionDate.value && presubmitCompleted;
    }

    // reloadMissionPage() {
    //     this.missionDialoguesService.showMissionDetailsThroughState(
    //         this.mission?.id
    //     );
    // }

    save() {
        return this.saveAndSetupMission().catch(error => {
            this.logging.error(error, `Error while saving: ${error.message}`);
            return Promise.reject(error);
        });
    }

    saveAndClose() {
        this.save().then(results => this.modal.hide());
    }

    close() {
        this.modal.hide();
    }

    submit() {
        if (!this.isSubmittable) {
            this.logging.warn('This mission is missing information');
            return;
        }

        const doneWorking = this.workTracker.createTracker();

        this.saveAndSetupMission()
            .then(savedMission => {
                if (this.hasBlockingLotams) {
                    this.logging.error(
                        null,
                        'There is a LOTAM over the operating area preventing this mission from proceeding.'
                    );
                    return;
                }

                if (!this.allLotamsAcknowledgedByMissionPlanner) {
                    this.logging.error(
                        null,
                        'There are unacknowledged LOTAMs. Please ensure the RPIC acknowledges all LOTAMs before trying again.'
                    );
                    return;
                }

                if (!this.allOverlapsAcknowledgedByCurrentUser) {
                    this.logging.error(
                        null,
                        'There are unacknowledged conflicting missions. Please acknowledge all conflicting missions before trying again.'
                    );
                    return;
                }

                if (this.submitOption === SubmitOption.FLY) {
                    return this.missionService
                        .prepareToFly(this.savedMission.id)
                        .then(
                            updatedMission => {
                                this.setupMission(updatedMission);
                                this.missionForm.markAsPristine();
                                if (
                                    updatedMission.status !==
                                    DisplayableMissionDto.Status.READY_TO_FLY
                                ) {
                                    this.logging.warn(
                                        'For some reason the mission did not become ready to fly'
                                    );
                                    return;
                                }
                                this.logging.success('Mission is ready to fly');
                                this.fieldAppHandoff();
                                return;
                            },
                            error => {
                                this.logging.error(error);
                            }
                        );
                }

                const draftApprovals =
                    this.savedMission.approvals?.filter(
                        a =>
                            a.status ===
                            RequestersMissionApprovalDto.Status.DRAFT
                    ) ?? [];
                const approvalsInProgress =
                    this.savedMission.approvals?.filter(
                        a =>
                            a.status ===
                                RequestersMissionApprovalDto.Status
                                    .BEING_REVIEWED ||
                            a.status ===
                                RequestersMissionApprovalDto.Status.PENDING
                    ) ?? [];

                if (draftApprovals.length > 0) {
                    return firstValueFrom(
                        combineLatest(
                            draftApprovals.map(approval =>
                                this.missionApprovalService.submitForApproval(
                                    approval.id,
                                    []
                                )
                            )
                        ).pipe(takeUntil(this.ngUnsubscribe$))
                    ).then(
                        approval => {
                            this.refreshSavedMission('approval');
                            this.logging.success(
                                'Mission submitted for approval'
                            );
                        },
                        error => {
                            this.logging.error(
                                error,
                                'Error submitting mission for approval'
                            );
                        }
                    );
                } else if (
                    draftApprovals.length === 0 &&
                    approvalsInProgress.length > 0
                ) {
                    // Can not submit in this state
                    this.logging.success(
                        'Mission approval is already in progress'
                    );
                    return;
                }
            })
            .finally(() => {
                this.setupGuideChecklistService.completeActivityTask(
                    SetupGuideActivityDto.StepIdentifier.SUBMIT_MISSION,
                    this.savedMission.id
                );
                doneWorking();
            });
    }

    onMissionWorkflowChanged() {
        this.updateMissionWorkflowVersionReference();
        if (this.skipLoadingWorkflows) {
            this.skipLoadingWorkflows = false;
            return;
        }
        if (this.savedMission == null) {
            return;
        }

        if (this.savedMission.missionWorkflowVersionId == null) {
            this.initiateWorkflow();
        } else {
            // do-nothing
        }
        this.updateAuthorityRegisterStatuses();
    }

    private updateMissionWorkflowVersionReference() {
        if (this.missionWorkflows == null) {
            return;
        }
        this.missionDetails.missionWorkflowVersion = this.missionWorkflows.find(
            wf => wf.id === this.missionWorkflowVersionId.value
        );
        this.generateAuthorityIndicatorLookup();
        this.updateAdditionalAuthorities();
    }

    private refreshCurrentAuthority() {
        if (
            this.missionDetails.missionWorkflowVersion == null ||
            this.missionDetails.missionWorkflowVersion.delegatedAuthority ==
                null
        ) {
            this.missionEditService.setLoadingWorkflowTracker(false);
            return;
        }

        const authorityId =
            this.missionDetails.missionWorkflowVersion.delegatedAuthority.id;
        const organisationId =
            this.savedMission?.organisationId ?? this.organisation.id;

        this.organisationAuthorityService
            .findAuthority(authorityId, organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: authority => {
                    this.missionWorkflowPrimaryAuthority = authority;
                    if ('requiresApprovalPolicy' in authority) {
                        this.authorityRegistersSetUp =
                            authority.registers.filter(r => r.isSetup === true)
                                .length === authority.registers.length;

                        const pilotRegister = authority.registers.find(
                            r =>
                                r.registerEntityType ===
                                AuthorityTypeRegisterConditionsDto
                                    .RegisterEntityType.PILOT
                        );

                        const rpaRegister = authority.registers.find(
                            r =>
                                r.registerEntityType ===
                                AuthorityTypeRegisterConditionsDto
                                    .RegisterEntityType.RPA
                        );

                        const personnelSetup = pilotRegister?.isSetup === false;
                        const rpaSetup = rpaRegister?.isSetup === false;
                        const message =
                            personnelSetup && rpaSetup
                                ? 'The Remote Pilot Register and RPA Register are not configured correctly.'
                                : personnelSetup
                                ? 'Remote Pilot Register is not configured correctly.'
                                : 'RPA Register is not configured correctly.';
                        this.authorityRegistersActionMessage = message;
                        this.primaryAuthorityPilotRegisterDisabled =
                            pilotRegister?.registerMode ===
                            AuthorityRegisterSummaryDto.RegisterMode.DISABLED;
                        this.primaryAuthorityRpaRegisterDisabled =
                            rpaRegister?.registerMode ===
                            AuthorityRegisterSummaryDto.RegisterMode.DISABLED;

                        const authoritiesWithPilotRegisters =
                            this.authoritiesWithPilotRegistersEnabled.getValue();
                        const authoritiesWithRpaRegisters =
                            this.authoritiesWithRpaRegistersEnabled;

                        const addOrRemoveRegisterFromList = (
                            registerDisabled: boolean,
                            enabledList: number[]
                        ) => {
                            if (
                                !registerDisabled &&
                                !enabledList.includes(authority.id)
                            ) {
                                enabledList.push(authority.id);
                            } else if (
                                registerDisabled &&
                                enabledList.includes(authority.id)
                            ) {
                                const index = enabledList.findIndex(
                                    id => id === authority.id
                                );
                                enabledList.splice(index, 1);
                            }
                        };

                        addOrRemoveRegisterFromList(
                            this.primaryAuthorityPilotRegisterDisabled,
                            authoritiesWithPilotRegisters
                        );
                        addOrRemoveRegisterFromList(
                            this.primaryAuthorityRpaRegisterDisabled,
                            authoritiesWithRpaRegisters
                        );

                        this.authoritiesWithPilotRegistersEnabled.next(
                            authoritiesWithPilotRegisters
                        );
                        this.authoritiesWithRpaRegistersEnabled =
                            authoritiesWithRpaRegisters;
                    }
                    this.updateMissionApprovals()
                        .pipe(
                            mergeMap(approvals => this.refreshDocumentation())
                        )
                        .subscribe(DO_NOTHING)
                        .add(this.workTracker.createTracker());
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error setting up workflow authority: ${error.message}`
                    );
                },
                complete: () => {
                    this.missionEditService.setLoadingWorkflowTracker(false);
                }
            })
            .add(this.workTracker.createTracker());
    }

    /**
     * A function that is called when the workflow on the mission is changed or updated.
     * It filters mission additional authorities against those on the workflow to prevent duplicates.
     * It also adjusts the available additional authorities list based on which are already on the workflow.
     */
    updateAdditionalAuthorities() {
        // Find additional authorities from the workflow
        const additionalWorkflowAuthorities =
            this.missionDetails?.missionWorkflowVersion
                ?.additionalAuthorityIds ?? [];
        // Remove mission additional authorities that are on the workflow
        this.missionForm.value.additionalAuthorityIds.forEach((id, index) => {
            if (additionalWorkflowAuthorities.includes(id)) {
                this.missionForm.controls.additionalAuthorityIds.removeAt(
                    index
                );
            }
        });
        // Update available additional authorities list.
        this.availableAdditionalAuthorities =
            this.allAdditionalAuthorities.filter(
                a =>
                    additionalWorkflowAuthorities.find(id => id === a.id) ==
                        null &&
                    this.missionForm.value.additionalAuthorityIds.find(
                        id => id === a.id
                    ) == null
            );
    }

    addAdditionalAuthority() {
        const id = this.newAdditionalAuthorityId;
        this.newAdditionalAuthorityId = null;
        const newAdditionalAuthority = this.allAdditionalAuthorities.find(
            a => a.id === id
        );
        this.availableAdditionalAuthorities =
            this.availableAdditionalAuthorities.filter(a => a.id !== id);
        const control = new FormControl(id);
        this.missionForm.controls.additionalAuthorityIds.push(control);
        control.markAsDirty();
        if (newAdditionalAuthority.requiresApproval) {
            const doneWorking = this.workTracker.createTracker();

            this.missionEditService
                .saveMission()
                .then(mission => {
                    return this.createApprovals(mission);
                })
                .then(() =>
                    firstValueFrom(
                        this.updateMissionApprovals().pipe(
                            mergeMap(approvals => this.refreshDocumentation())
                        )
                    )
                )
                .finally(() => {
                    doneWorking();
                });
        }
        this.generateAuthorityIndicatorLookup();
    }

    removeAdditionalAuthority(id: number) {
        const index = this.missionForm.value.additionalAuthorityIds.findIndex(
            a => a === id
        );
        const removedAdditionalAuthority = this.allAdditionalAuthorities.find(
            a => a.id === id
        );
        const authorities = this.availableAdditionalAuthorities.concat(
            this.allAdditionalAuthorities.find(a => a.id === id)
        );
        const additionalWorkflowAuthorities =
            this.missionDetails.missionWorkflowVersion?.additionalAuthorityIds;

        this.availableAdditionalAuthorities = this.allAdditionalAuthorities
            .filter(
                a =>
                    additionalWorkflowAuthorities.find(id => id === a.id) ==
                    null
            )
            .filter(a => authorities.find(a2 => a2.id === a.id) != null);

        this.missionForm.controls.additionalAuthorityIds.removeAt(index);
        this.missionForm.controls.additionalAuthorityIds.markAsDirty();
        if (removedAdditionalAuthority.requiresApproval) {
            const approvalToCancel = this.savedMission.approvals
                ?.filter(
                    a =>
                        a.status !==
                        RequestersMissionApprovalDto.Status.CANCELLED
                )
                .find(a => a.authorityId === removedAdditionalAuthority.id);
            if (approvalToCancel == null) {
                return;
            }
            const doneWorking = this.workTracker.createTracker();

            this.missionEditService
                .saveMission()
                .then(mission =>
                    firstValueFrom(this.cancelApproval(approvalToCancel.id))
                )
                .then(refreshedMission => this.setupMission(refreshedMission))
                .catch((error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error setting up mission: ${error.message}`
                    )
                )
                .then(() => this.prefillPreviousDocumentation())
                .finally(() => {
                    doneWorking();
                });
        }
        this.generateAuthorityIndicatorLookup();
    }

    startAdditionalAuthorityApprovals() {
        if (this.savedMission == null) {
            this.save().then(mission => this.createApprovals(mission));
        } else {
            this.createApprovals(this.savedMission);
        }
    }

    createApprovals(mission: DisplayableMissionDto) {
        return firstValueFrom(
            this.missionApprovalService.createMissionApproval(mission.id).pipe(
                switchMap(approvals => {
                    return this.missionService
                        .findMission(mission.id, this.organisation.id)
                        .pipe(takeUntil(this.ngUnsubscribe$));
                }),
                takeUntil(this.ngUnsubscribe$)
            )
        )
            .then(refreshedMission => this.setupMission(refreshedMission))
            .catch((error: FlyFreelyError) =>
                this.logging.error(
                    error,
                    `Error setting up mission: ${error.message}`
                )
            )
            .then(() => this.prefillPreviousDocumentation());
    }

    cancelApproval(approvalId: number) {
        return this.missionApprovalService.cancelApproval(approvalId).pipe(
            switchMap(approvals => {
                return this.missionService
                    .findMission(this.savedMission.id, this.organisation.id)
                    .pipe(takeUntil(this.ngUnsubscribe$));
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    updateMissionApprovals() {
        const allApprovals = this.savedMission?.approvals ?? [];
        // Check if any approval authorities need to be fetched from the server.
        const approvalsWithoutAuthorityData = allApprovals
            .reduce(
                (acc, a) =>
                    acc.some(approval => a.authorityId === approval.authorityId)
                        ? acc
                        : acc.concat(a),
                [] as typeof allApprovals
            )
            .filter(
                a =>
                    this.missionAuthorities.find(
                        auth => auth.id === a.authorityId
                    ) == null
            );
        if (approvalsWithoutAuthorityData.length === 0) {
            return of(null);
        }
        const managingOrganisationId =
            this.savedMission?.organisationId ?? this.organisation.id;
        return combineLatest(
            approvalsWithoutAuthorityData.map(a =>
                this.organisationAuthorityService.findAuthority(
                    a.authorityId,
                    managingOrganisationId
                )
            )
        ).pipe(
            tap({
                next: authorities => {
                    this.missionAuthorities.concat(authorities);
                    // This builds all of the approvals on the mission
                    // There is a separate activeMissionApprovals() getter that will return only the active approvals if we need those.
                    this.missionApprovals = allApprovals.map(approval => {
                        const authority = authorities.find(
                            a => a.id === approval.authorityId
                        );
                        const authorityType = this.missionAuthorityGroups.find(
                            a => a.id === authority.authorityTypeId
                        );
                        return {
                            approval: approval,
                            authority: authority,
                            authorityType: authorityType
                        };
                    });
                    this.activeApprovals = findActiveMissionApprovals(
                        this.missionApprovals,
                        true
                    );
                    this.lockEditFields =
                        !this.savedMission.availableActions.canEdit ||
                        this.missionEditService.isReadOnly ||
                        (this.activeApprovals?.length > 1 &&
                            this.activeApprovals.some(
                                a =>
                                    a.approval.status ===
                                        ApproversMissionApprovalDto.Status
                                            .APPROVED ||
                                    a.approval.status ===
                                        ApproversMissionApprovalDto.Status
                                            .ACCEPTED ||
                                    a.approval.status ===
                                        ApproversMissionApprovalDto.Status
                                            .BEING_REVIEWED
                            ));
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error fetching approval authorities: ${error.message}`
                    )
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    /**
     * Takes an approval and determines if the approval is related to the primary authority on a workflow, any of the workflow's secondary authorities, or any additional authorities. Returns a string identifying the source of the approval that can be displayed in the template as-is.
     * @param approval An approval on the mission
     * @returns A string identifying the source of the approval
     */
    getApprovalSource(approval: RequestersMissionApprovalDto) {
        const workflowPrimaryAuthority =
            this.savedMission.missionWorkflowVersion?.delegatedAuthority;
        const workflowSecondaryAuthorities =
            this.savedMission.missionWorkflowVersion?.additionalAuthorities;
        const additionalAuthorities = this.savedMission.additionalAuthorities;
        return approval.authorityId === workflowPrimaryAuthority?.id
            ? 'Workflow Primary Authority'
            : workflowSecondaryAuthorities.find(
                  a => a.id === approval.authorityId
              ) != null
            ? 'Workflow Secondary Authority'
            : additionalAuthorities.find(a => a.id === approval.authorityId) !=
              null
            ? 'Additional Authority'
            : 'Unknown';
    }

    initiateWorkflow() {
        this.missionEditService.setLoadingWorkflowTracker(true);
        const doneWorking = this.workTracker.createTracker();

        this.missionEditService
            .saveMission()
            .then(mission => {
                if (
                    (mission.missionWorkflowVersion.delegatedAuthority ==
                        null ||
                        !mission.missionWorkflowVersion.delegatedAuthority
                            .requiresApproval) &&
                    (mission.missionWorkflowVersion.additionalAuthorities ==
                        null ||
                        mission.missionWorkflowVersion.additionalAuthorities.filter(
                            a => a.requiresApproval
                        ).length === 0)
                ) {
                    return this.setupMission(mission);
                }
                return this.createApprovals(mission);
            })
            .then(() => {
                this.stepCompleted('missionWorkflow');
                this.changeDetector.detectChanges();
                this.refreshCurrentAuthority();
            })
            .finally(() => {
                doneWorking();
            });
    }

    changeWorkflow() {
        if (
            (this.savedMission.missionWorkflowVersion.delegatedAuthority !=
                null &&
                this.savedMission.missionWorkflowVersion.delegatedAuthority
                    .requiresApproval) ||
            (this.savedMission.missionWorkflowVersion.additionalAuthorities !=
                null &&
                this.savedMission.missionWorkflowVersion.additionalAuthorities.filter(
                    a => a.requiresApproval
                ).length > 0)
        ) {
            const authorityIds = [
                this.savedMission.missionWorkflowVersion.delegatedAuthority.id
            ].concat(
                this.savedMission.missionWorkflowVersion?.additionalAuthorities?.map(
                    a => a.id
                ) ?? []
            );
            const approvalsRelatedToAuthority = findActiveApprovals(
                this.savedMission.approvals,
                true
            ).reduce(
                (acc, a) =>
                    authorityIds.includes(a.authorityId) ? acc.concat(a) : acc,
                []
            );
            if (
                approvalsRelatedToAuthority != null &&
                approvalsRelatedToAuthority.length > 0
            ) {
                forkJoin(
                    approvalsRelatedToAuthority.map(a =>
                        this.cancelApproval(a.id)
                    )
                )
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: () => this.initiateWorkflow(),
                        error: (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                `Error cancelling approvals: ${error.message}`
                            )
                    });
            } else {
                this.initiateWorkflow();
            }
        } else {
            this.initiateWorkflow();
        }
    }

    undoWorkflowChange() {
        this.missionForm.patchValue({
            missionWorkflowVersionId: this.savedMission.missionWorkflowVersionId
        });
    }

    returnMissionRuleset() {
        return this.missionDetails?.missionWorkflowVersion?.delegatedAuthority
            ?.authorityType?.ruleset;
    }

    showAuthorityDetails(authority: SimpleAuthorityDto) {
        this.angulartics2.eventTrack.next({
            action: 'primary-authority-view',
            properties: {
                category: SCREEN_IDENTIFIER
            }
        });
        this.organisationAuthorityService
            .findAuthority(authority.id, this.savedMission.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: relativeAuthority => {
                    const modal =
                        this.authorityDialoguesService.showAuthorityDetails(
                            this.organisation.id,
                            authority.authorityType,
                            relativeAuthority
                        );
                    modal.content.onShowRegister
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(payload => {
                            this.showRegister(
                                <AuthorityTypeDto>payload.authorityType,
                                payload.authority,
                                payload.register
                            );
                        });
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while refreshing authority details: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    showRegister(
        authorityType: AuthorityTypeDto,
        authority: OrganisationAuthorityDto,
        register: AuthorityRegisterSummaryDto
    ) {
        if (
            register.registerEntityType ===
            AuthorityRegisterSummaryDto.RegisterEntityType.RPA
        ) {
            this.authorityDialoguesService.showRpaRegisterDialogue(
                this.organisation.id,
                authorityType,
                authority,
                register,
                this.organisation.id
            );
        } else {
            this.authorityDialoguesService.showPersonnelRegisterDialogue(
                this.organisation.id,
                authorityType,
                <OrganisationAuthorityDto>authority,
                register,
                this.organisation.id
            );
        }
    }

    showRpaDetails(i: number) {
        const rpaId = this.craftIds.value[i];
        this.rpaDialoguesService.showCraftDetails(
            rpaId,
            this.organisation.id,
            false
        );
    }

    showEquipmentDetails(i: number) {
        const equipmentId = this.equipmentIds.value[i];
        this.equipmentDialoguesService.showEquipmentDetails(
            equipmentId,
            this.organisation,
            false
        );
    }

    showPersonDetails(i: number) {
        this.personDialoguesService.showPersonDetailsDialogue(
            i,
            this.organisation.id,
            false
        );
    }

    updateIsResourcesCompleted() {
        this.isResourcesCompleted =
            this.missionTypeId.valid &&
            this.missionWorkflowVersionId.valid &&
            this.craftIds.valid &&
            this.rpic.valid;
        this.updateAuthorisationRequirementsComplete();

        this.changeDetector.detectChanges();
    }

    scrollTo(section: string) {
        this.activeTab(section);
        document
            .querySelector(`#${section}`)
            .scrollIntoView({ behavior: 'smooth' });
    }

    onSectionChange(sectionId: string) {
        this.activeTab(sectionId);
    }

    activeTab(section: string) {
        this.isActive = {};
        this.isActive[section] = true;
    }

    showSelectPrefillMission() {
        this.isShowingSelectPrefillMission = true;
        this.cancelStack.push(() => this.hideSelectPrefillMission());

        this.angulartics2.eventTrack.next({
            action: 'documentation-prefill-show',
            properties: {
                category: SCREEN_IDENTIFIER
            }
        });
    }

    hideSelectPrefillMission() {
        this.isShowingSelectPrefillMission = false;
        this.cancelStack.pop();
    }

    showAerodromeSearch(index?: number) {
        if (index != null) {
            const aerodrome: ContactDto =
                this.missionForm.controls.aerodromesOfInterest.value[index];
            this.defaultAerodromeCode = aerodrome.identifier;
        }
        this.isAddingAerodromes = true;
        this.cancelStack.push(() => this.hideAerodromeSearch());
    }

    hideAerodromeSearch() {
        this.defaultAerodromeCode = null;
        this.isAddingAerodromes = false;
        this.cancelStack.pop();
    }

    showNotamSearch() {
        this.isAddingNotams = true;
        this.cancelStack.push(() => this.hideNotamSearch());
    }

    hideNotamSearch() {
        this.isAddingNotams = false;
        this.cancelStack.pop();
    }

    acknowledgeNoRelevantNotam() {
        this.missionForm.patchValue({
            declareNoRelevantNotam: true
        });
        if (this.savedMission?.id == null) {
            return;
        }
        this.missionService
            .acknowledgeNoRelevantNotam(
                this.savedMission.id,
                this.userService.getCurrentUser().id
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.saveAndSetupMission())
            .add(this.workTracker.createTracker());
    }

    showAirspaceAuthorisation(requestNew = false) {
        if (this.working) {
            return;
        }
        const mission = this.missionForm.value as MissionFormValue;

        const command: AirspaceCheckCommand =
            this.airspaceCheckService.setupCheckCommand(
                this.rpas.filter(r => mission.craftIds.includes(r.id)),
                this.missionWorkflows,
                mission,
                this.location,
                this.rpaTypes.reduce(toLookup, {}),
                true,
                false
            );
        const doneWorking = this.workTracker.createTracker();
        this.missionEditService
            .saveMission()
            .then(savedMission => {
                doneWorking();
                this.airspaceAuthorisationManager.updateMission(savedMission);
                this.angulartics2.eventTrack.next({
                    action:
                        // FIXME not sure about this one
                        this.latestAuthorisations == null
                            ? 'show-request-authorisation'
                            : 'view-authorisation',
                    properties: {
                        category: 'airspace-authorisation'
                    }
                });
                const modal =
                    this.airspaceDialoguesService.showAirspaceAuthorisationDialogue(
                        this.airspaceAuthorisationManager,
                        this.airspaceCheckService,
                        this.organisation,
                        command,
                        savedMission,
                        null,
                        null,
                        this.rpaTypes,
                        requestNew
                    );
                modal.content.missionUpdated
                    .pipe(
                        takeUntil(modal.onHide),
                        takeUntil(this.ngUnsubscribe$)
                    )
                    .subscribe(mission => {
                        // No need to setup the mission again if nothing changed
                        if (
                            mission.modificationTime ===
                            this.savedMission.modificationTime
                        ) {
                            return;
                        }
                        this.setupMission(mission);
                    });
                modal.content.missionTimeUpdated
                    .pipe(
                        takeUntil(modal.onHide),
                        takeUntil(this.ngUnsubscribe$)
                    )
                    .subscribe(time => {
                        this.missionForm.patchValue({
                            missionDate: time.startTime,
                            missionEstimatedTime: time.duration
                        });
                        this.save();
                    });
                modal.onHidden
                    .pipe(takeUntil(this.ngUnsubscribe$), take(1))
                    .subscribe(() => {
                        this.missionService
                            .findMission(savedMission.id, this.organisation.id)
                            .pipe(takeUntil(this.ngUnsubscribe$))
                            .subscribe({
                                next: updatedMission => {
                                    this.airspaceAuthorisationManager.updateMission(
                                        updatedMission
                                    );
                                    // No need to setup the mission again if nothing changed
                                    if (
                                        updatedMission.modificationTime ===
                                        this.savedMission.modificationTime
                                    ) {
                                        return;
                                    }
                                    this.setupMission(updatedMission);
                                },
                                error: (error: FlyFreelyError) =>
                                    this.logging.error(
                                        error,
                                        `Error while updating mission: ${error.message}`
                                    )
                            })
                            .add(this.workTracker.createTracker());
                    });
            })
            .catch((error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error updating mission: ${error.message}`
                );
                doneWorking();
            });
    }

    cancelAirspaceAuthorisation(authorisation: AirspaceAuthorisationDto) {
        this.angulartics2.eventTrack.next({
            action: 'cancel-authorisation',
            properties: {
                category: 'airspace-authorisation'
            }
        });
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Cancel Airspace Authorisation?',
                `Are you sure you want to cancel this airspace authorisation`,
                'Yes',
                () => Promise.resolve()
            )
            .then(() => {
                this.airspaceAuthorisationManager
                    .cancelAuthorisation(
                        authorisation.id,
                        authorisation.authorisationType
                    )
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: authorisation => {
                            this.missionService
                                .findMission(
                                    this.savedMission.id,
                                    this.organisation.id
                                )
                                .pipe(takeUntil(this.ngUnsubscribe$))
                                .subscribe(mission =>
                                    this.setupMission(mission)
                                )
                                .add(this.workTracker.createTracker());
                        },
                        error: (error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                `Error cancelling airspace authorisation: ${error.message}`
                            );
                        }
                    })
                    .add(this.workTracker.createTracker());
            }, DO_NOTHING);
    }

    refreshAirspaceAuthorisation() {
        if (this.savedMission == null || this.mission?.id == null) {
            return;
        }
        this.airspaceAuthorisationManager.findAuthorisationsByMissionId(
            this.savedMission?.id ?? this.mission.id
        );
    }

    requestNewAirspaceAuthorisation() {
        this.latestAuthorisations = null;
        this.angulartics2.eventTrack.next({
            action: 'show-request-new-authorisation',
            properties: {
                category: 'airspace-authorisation'
            }
        });
        this.showAirspaceAuthorisation(true);
    }

    onPrefillFromMission(mission: MissionSummaryDto) {
        this.isShowingSelectPrefillMission = false;

        const doneWorking = this.workTracker.createTracker();
        this.prefillFromMission(mission.id).then(
            () => {
                this.logging.success('Documentation prefilled');
                doneWorking();
            },
            (error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error while loading mission: ${error.message}`
                );
                doneWorking();
            }
        );

        this.angulartics2.eventTrack.next({
            action: 'documentation-prefill',
            properties: {
                category: SCREEN_IDENTIFIER
            }
        });
    }

    /**
     * Update the map and other geographic bearings based on the selected location, aerodromes and NOTAMs.
     * @param location the currently selected location
     * @param aerodromes the list of aerodromes
     * @param notams the selected NOTAMs
     * @param notams the selected LOTAMs
     */
    private updateGeographicInformation(
        aerodromes: ContactWithLocationDto[],
        notams: NotamDto[],
        lotams: MissionNoticeDto[]
    ) {
        const maybeFlightArea = flightAreaOf(this.location);

        const centre: GeoJSON.Point | null = pipe(
            maybeFlightArea,
            optionMap(
                (l: LocationFeatureDto) =>
                    center(<GeoJSON.Polygon>l.geometry).geometry
            ),
            // Fallback if there is no polygon
            alt(() =>
                optionMap((f: LocationFeatureDto) => <GeoJSON.Point>f.geometry)(
                    flightAreaPointOfFeatures(this.location?.features ?? [])
                )
            ),
            getOrElse(() => null)
        );

        const locationArea = getOrElse(() => null)(
            optionMap((l: LocationFeatureDto) =>
                area(<GeoJSON.Polygon>l.geometry)
            )(maybeFlightArea)
        );

        const getBearing = (pos: GeoJSON.Point) =>
            ({
                bearing: bearingToAzimuth(bearing(pos, centre)),
                distance: distance(centre, pos) * 0.539957
            } as LocationBearing);

        const getLotamBearing = (notice: NoticeDto): LocationBearing => {
            if (!notice.location || notice.location.length === 0) {
                return null;
            }
            const ptLo = notice.location.find(f => f.geometry.type === 'Point');
            if (ptLo) {
                return getBearing(ptLo.geometry as GeoJSON.Point);
            }

            const polyLo = notice.location.find(
                f => f.geometry.type === 'Polygon'
            );
            if (polyLo) {
                const centroid = center(polyLo.geometry as GeoJSON.Polygon)
                    .geometry as GeoJSON.Point;
                return getBearing(centroid);
            }

            // return null if we can't find a Point location and Polygon location
            return null;
        };

        // sort notams to closest first
        notams = notams.sort(
            (a, b) =>
                getBearing(a.position).distance -
                getBearing(b.position).distance
        );
        if (this.missionForm.controls.notams.value !== notams) {
            this.missionForm.controls.notams.patchValue(notams);
        }
        this.locationBearings = {
            notams:
                notams != null
                    ? notams
                          .filter(n => n.position != null)
                          .map(n =>
                              n.position != null ? getBearing(n.position) : null
                          )
                    : [],
            lotams:
                lotams != null
                    ? lotams.map(l => getLotamBearing(l.notice))
                    : [],
            aerodromes:
                aerodromes != null
                    ? aerodromes
                          .filter(a => a.location != null)
                          .map(a => getBearing(a.location))
                    : [],
            locationCentre: centre,
            locationArea: locationArea
        };
    }

    setSunriseSunset(sunriseSunset: SunriseSunset) {
        this.sunriseSunset = sunriseSunset;
        const timeZone =
            this.lockedFields.timeZone || this.timeZone.value == null
                ? this.location.timeZone
                : this.timeZone.value;
        this.sunrise = toLocalTime(
            sunriseSunset.sunrise,
            timeZone,
            this.dateFormatService.getTimeFormat()
        );
        this.sunset = toLocalTime(
            sunriseSunset.sunset,
            timeZone,
            this.dateFormatService.getTimeFormat()
        );
        this.civilTwilightStart = toLocalTime(
            sunriseSunset.civilTwilightStart,
            timeZone,
            this.dateFormatService.getTimeFormat()
        );
        this.civilTwilightEnd = toLocalTime(
            sunriseSunset.civilTwilightEnd,
            timeZone,
            this.dateFormatService.getTimeFormat()
        );
        this.calculateTimeOfDay();
    }

    calculateTimeOfDay() {
        if (this.sunriseSunset == null) {
            return;
        }
        const civilStart = this.sunriseSunset.civilTwilightStart;
        const civilEnd = this.sunriseSunset.civilTwilightEnd;
        const startTimeInDay = moment(this.missionStartTime).isBetween(
            moment(civilStart),
            moment(civilEnd)
        );
        const endTimeInDay = moment(this.missionEndTime).isBetween(
            moment(civilStart),
            moment(civilEnd)
        );
        const timeOfDay =
            startTimeInDay && endTimeInDay
                ? DisplayableMissionDto.TimeOfDay.DAY
                : !startTimeInDay && !endTimeInDay
                ? DisplayableMissionDto.TimeOfDay.NIGHT
                : DisplayableMissionDto.TimeOfDay.DAY_NIGHT;
        if (timeOfDay !== this.missionForm.controls.timeOfDay.value) {
            this.missionForm.controls.timeOfDay.patchValue(timeOfDay);
        }
        this.changeDetector.detectChanges();
    }

    private generateAuthorityIndicatorLookup() {
        this.authorityIndicatorLookup = {};
        if (
            this.missionDetails.missionWorkflowVersion == null ||
            this.missionDetails.missionWorkflowVersion.delegatedAuthority ==
                null
        ) {
            return;
        }
        let currentLabelIndex = 0;
        if (this.missionDetails.missionWorkflowVersion.delegatedAuthority?.id) {
            this.authorityIndicatorLookup[
                this.missionDetails.missionWorkflowVersion.delegatedAuthority?.id
            ] = String.fromCharCode(65 + currentLabelIndex); // A
            currentLabelIndex++;
        }

        // B, C, D... - Workflow Additional Authorities
        if (this.missionDetails.missionWorkflowVersion?.additionalAuthorities) {
            this.missionDetails.missionWorkflowVersion.additionalAuthorities.forEach(
                authority => {
                    if (authority.id) {
                        this.authorityIndicatorLookup[authority.id] =
                            String.fromCharCode(65 + currentLabelIndex);
                        currentLabelIndex++;
                    }
                }
            );
        }

        // B, C, D... - Mission Additional Authorities
        if (this.missionForm?.value?.additionalAuthorityIds) {
            this.missionForm.value.additionalAuthorityIds.forEach(
                authorityId => {
                    if (
                        authorityId &&
                        !this.authorityIndicatorLookup[authorityId]
                    ) {
                        this.authorityIndicatorLookup[authorityId] =
                            String.fromCharCode(65 + currentLabelIndex);
                        currentLabelIndex++;
                    }
                }
            );
        }
    }

    get missionTypeId() {
        return this.missionForm.get('missionTypeId');
    }

    get missionWorkflowVersionId() {
        return this.missionForm.get('missionWorkflowVersionId');
    }

    get craftIds() {
        return this.missionForm.get('craftIds');
    }

    get equipmentIds() {
        return this.missionForm.get('equipmentIds');
    }

    get crewNotes() {
        return this.missionForm.get('crewNotes');
    }

    get missionDate() {
        return this.missionForm.get('missionDate');
    }

    get timeZone() {
        return this.missionForm.get('timeZone');
    }

    get maximumHeight() {
        return this.missionForm.get('maximumHeight');
    }

    get emergencyContacts() {
        return this.missionForm.get('emergencyContacts');
    }

    get pointOfContact() {
        return this.missionForm.get('pointOfContact');
    }

    get pointOfContactInformation() {
        return this.missionForm.get('pointOfContactInformation');
    }

    get missionEmergencyContacts() {
        return this.missionForm.get('missionEmergencyContacts');
    }

    get radioFrequencies() {
        return this.missionForm.get('radioFrequencies');
    }

    get missionRadioFrequencies() {
        return this.missionForm.get('missionRadioFrequencies');
    }

    get aerodromesOfInterest() {
        return this.missionForm.get('aerodromesOfInterest');
    }

    get visualLineOfSight() {
        return this.missionForm.get('visualLineOfSight');
    }

    get timeOfDay() {
        return this.missionForm.get('timeOfDay');
    }

    get missionCrew() {
        return this.missionForm.get('missionCrew');
    }

    get lotams() {
        return this.missionForm.get('lotams');
    }

    get rpic() {
        return this.missionForm.get('rpic');
    }

    get missionEstimatedTime() {
        return this.missionForm.get('missionEstimatedTime');
    }

    get declareNoRelevantNotam() {
        return this.missionForm.get('declareNoRelevantNotam');
    }

    onFullscreenRequested() {
        this.fullScreenService.toggleFullScreen(this.mapElement);
    }

    fieldAppHandoff() {
        if (this.showHandoffPopup === true) {
            const date = fromTimestamp(
                this.savedMission.missionDate,
                this.savedMission.timeZone
            );
            const organisationId = this.organisation.id;

            const handoffPopup =
                this.missionDialoguesService.showFieldAppHandoff(
                    date,
                    this.savedMission.timeZone as string,
                    organisationId
                );

            handoffPopup.onHidden
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(() => this.modal.hide());
        } else {
            this.modal.hide();
        }
    }

    showAirspaceDisclaimerDialogue() {
        const disclaimer =
            this.location?.airspaceJurisdiction?.airspaceDisclaimer;
        const airspaceJurisdictionId = this.location?.airspaceJurisdiction?.id;
        if (
            airspaceJurisdictionId == null ||
            disclaimer == null ||
            disclaimer.length == 0
        ) {
            this.showAirspaceDisclaimer = false;
            return;
        }
        this.preferencesService
            .findPreferencesAsOption(AIRSPACE_DISCLAIMER, null)
            .pipe(
                map(p =>
                    getOrElse(() => ({
                        [airspaceJurisdictionId]: moment(new Date())
                            .subtract(1, 'day')
                            .toDate()
                    }))(p)
                ),
                takeUntil(this.ngUnsubscribe$),
                take(1)
            )
            .subscribe({
                next: preferences => {
                    this.airspaceDisclaimerDates = preferences;
                    if (
                        preferences == null ||
                        preferences[airspaceJurisdictionId] == null
                    ) {
                        this.showAirspaceDisclaimer = true;
                        return;
                    }
                    const today = moment(new Date()).format('YYYY-MM-DD');
                    const lastViewed = moment(
                        preferences[airspaceJurisdictionId]
                    ).format('YYYY-MM-DD');
                    // Only show the disclaimer once per account
                    this.showAirspaceDisclaimer =
                        moment(lastViewed).isBefore(today);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(error);
                }
            });
    }

    onAirspaceDisclaimerAcknowledged() {
        this.showAirspaceDisclaimer = false;
        const airspaceJurisdictionId = this.location?.airspaceJurisdiction?.id;
        this.preferencesService
            .updatePreferences(AIRSPACE_DISCLAIMER, null, {
                ...this.airspaceDisclaimerDates,
                [airspaceJurisdictionId]: new Date()
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(DO_NOTHING)
            .add(this.workTracker.createTracker());
    }

    /**
     * This checks if the workflow can be confirmed, acknowledged or changed based on the current state:
     * - The workflow is not currently loading
     * - The mission workflow field is not locked (preventing changes)
     * - A workflow has been selected (has a value)
     *
     * @returns {boolean} True if workflow state is valid, false otherwise
     */
    private workflowConfirmationAvailable() {
        return (
            !this.loadingWorkflow &&
            !this.lockedFields?.missionWorkflowVersion &&
            this.missionWorkflowVersionId.value != null
        );
    }

    /**
     * The "Confirm Workflow" button should show when an unsaved/cloned mission loads with a workflow already assigned
     * and when that assigned workflow requires approvals. The purpose of this is to create approvals for the mission before
     * the user gets down to the mission documentation step, where the approvals will influence which documents are required.
     * Confirming the workflow will save the mission with that currently selected workflow as the assigned workflow and create the approvals.
     */
    get showConfirmWorkFlow() {
        return (
            this.workflowConfirmationAvailable() &&
            this.approvalCreationRequired &&
            ((this.savedMission != null && this.savedMission.id == null) ||
                this.savedMission == null ||
                (this.savedMission != null &&
                    !this.missionWorkflowVersionId.valid))
        );
    }

    /**
     * Acknowledge workflow is similar to confirm workflow, but for workflows that don't require approval.
     * Acknowledging a workflow will save the mission with that currently selected workflow as the assigned workflow.
     */
    get showAcknowledgeWorkflow() {
        return (
            this.workflowConfirmationAvailable() &&
            !this.approvalCreationRequired &&
            ((this.savedMission != null && this.savedMission.id == null) ||
                this.savedMission == null ||
                (this.savedMission != null &&
                    !this.missionWorkflowVersionId.valid))
        );
    }

    /**
     * The "Change Workflow" button should show when the user selects a different workflow than the one already
     * saved to the mission.
     */
    get showChangeWorkflow() {
        return (
            this.workflowConfirmationAvailable() &&
            this.savedMission?.missionWorkflowVersionId != null &&
            this.savedMission.missionWorkflowVersionId !==
                this.missionWorkflowVersionId.value
        );
    }

    get approvalCreationRequired() {
        const workflow = this.missionDetails.missionWorkflowVersion;
        if (workflow == null) {
            return false;
        }

        const inactiveStatuses = [
            RequestersMissionApprovalDto.Status.CANCELLED,
            RequestersMissionApprovalDto.Status.REJECTED,
            RequestersMissionApprovalDto.Status.REVOKED,
            RequestersMissionApprovalDto.Status.REVISION_REQUIRED
        ];
        const activeApprovals =
            this.savedMission?.approvals?.filter(
                a => !inactiveStatuses.includes(a.status)
            ) ?? [];
        const showChangeWorkflow =
            this.savedMission != null &&
            this.savedMission.missionWorkflowVersionId != null &&
            this.savedMission.missionWorkflowVersionId !=
                this.missionWorkflowVersionId.value;
        return (
            !showChangeWorkflow &&
            this.missionDetails != null &&
            workflow != null &&
            this.savedMission != null &&
            workflow.id === this.missionWorkflowVersionId.value &&
            ((workflow.delegatedAuthority != null &&
                workflow.delegatedAuthority.requiresApproval === true &&
                activeApprovals.find(
                    approval =>
                        approval.authorityId === workflow.delegatedAuthority.id
                ) == null) ||
                (workflow.additionalAuthorities != null &&
                    workflow.additionalAuthorities.filter(
                        a =>
                            a.requiresApproval &&
                            activeApprovals.find(
                                approval => approval.authorityId === a.id
                            ) == null
                    ).length > 0))
        );
    }

    get additionalApprovalCreationRequired() {
        const additionalAuthoritiesWithApproval =
            this.missionForm.value.additionalAuthorityIds
                ?.map(id =>
                    this.allAdditionalAuthorities.find(auth => auth.id === id)
                )
                .filter(auth => auth?.requiresApproval) ?? [];
        const inactiveStatuses = [
            RequestersMissionApprovalDto.Status.CANCELLED,
            RequestersMissionApprovalDto.Status.REJECTED,
            RequestersMissionApprovalDto.Status.REVOKED,
            RequestersMissionApprovalDto.Status.REVISION_REQUIRED
        ];
        const activeApprovals =
            this.savedMission?.approvals?.filter(
                a => !inactiveStatuses.includes(a.status)
            ) ?? [];

        return (
            !this.approvalCreationRequired &&
            this.canUseAdditionalAuthorities &&
            additionalAuthoritiesWithApproval.length > 0 &&
            additionalAuthoritiesWithApproval.filter(
                a =>
                    activeApprovals.find(
                        approval => approval.authorityId === a.id
                    ) == null
            ).length > 0
        );
    }

    get hasPrefilledDocumentationResponses() {
        return (
            this.hasClonedFormResponses &&
            this.missionDocumentation?.documentationResponses != null &&
            Array.isArray(this.missionDocumentation.documentationResponses) &&
            this.missionDocumentation.documentationResponses.length > 0 &&
            this.missionDocumentation.documentationResponses.some(
                relatedEntity =>
                    relatedEntity.forms.some(
                        form => !form.completed && form.progress.progress > 0
                    )
            )
        );
    }

    get allLotamsAcknowledgedByMissionPlanner() {
        const lotams = this.missionForm.controls.lotams
            .value as MissionNoticeDto[];
        const currentUser = this.userService.getCurrentUser();
        const applicableLotams = lotams.filter(
            l => l.status === MissionNoticeDto.Status.APPLICABLE
        );
        return applicableLotams.every(l =>
            l.acknowledgementList.some(al => al.personId === currentUser.id)
        );
    }

    get hasBlockingLotams() {
        const lotams = this.missionForm.controls.lotams
            .value as MissionNoticeDto[];
        return lotams.some(l => l.status === MissionNoticeDto.Status.BLOCK);
    }

    get allOverlapsAcknowledgedByCurrentUser() {
        const overlaps = this.savedMission?.missionOverlapList ?? [];
        const currentUser = this.userService.getCurrentUser();
        return overlaps
            .filter(o => o.status === MissionOverlapDto.Status.APPLICABLE)
            .every(o =>
                o.acknowledgementList.some(al => al.personId === currentUser.id)
            );
    }

    // get missionApprovals() {
    //     return this.savedMission?.approvals;
    // }

    get activeMissionApprovals() {
        return findActiveMissionApprovals(this.missionApprovals, true);
    }

    /**
     * Does the mission have any approvals that aren't cancelled, rejected or revoked
     */
    get hasActiveApprovals() {
        return (
            this.missionApprovals != null &&
            this.missionApprovals.length > 0 &&
            findActiveMissionApprovals(this.missionApprovals, true).length > 0
        );
    }
}
